import { defineStore } from "pinia";
import { AnalyticsReportElementAddType, AnalyticsReportElementType, AnalyticsV2ChartType, AnalyticsV2ElementType } from "@/models/etc/types";
import { CommonHttp } from "@/models/interfaces/http/common.http.request";
import { reactive, ref, nextTick } from "vue";
import { AnalyticsRequestData } from "./interfaces/types.analytics";
import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import { rootStore } from './index'
import { BaseListItem, getIdentityKind } from "@/components/common/base-list/interfaces/ListTypes";
import { findRecursive, foreachRecursive, mapRecursive } from "@/models/helpers/recursive";
import { settingsStore } from "./settings";
import { graphSettingsStore } from "./graph";
import { queryStore } from "./querytable";
import { AnalyticsReportBreak, AnalyticsReportElement, AnalyticsReportLinkToChart, 
  AnalyticsReportLinkToElement, AnalyticsReportLinkToList, AnalyticsReportLinkToProjection, AnalyticsReportLinkToSelection } from "@/models/interfaces/entity-settings/analyticreports/reports";
import {
  AnalyticsChartIdentity,
  AnalyticsElementIdentity,
  AnalyticsElementKind,
  AnalyticsFolderIdentity,
  AnalyticsElementsToRootListMoveRequest,
  AnalyticsElementsToFolderMoveRequest,
  AnalyticsElementsToNewParentMoveRequest,
} from "@/models/interfaces/entity-settings/charts/charts";
import { MoveTargetKind } from "./interfaces/types.analytics";
import { CHILD_ARRAYS } from "@/components/common/base-list/dndRules";
import { insertAnimActive } from "@/components/common/base-list/insertAnimState";
import { tokenizeSearch, makeVariants, makeTokenVariants, matchesTokens } from "@/components/common/basic/search-highlight/searchHighlight";

const API_URL = process.env.VUE_APP_API_URL
const FAVORITE_MODE_STORAGE_KEY = "analytics.favoriteMode";
// Одноразовый флаг: после clone не раскрываем путь при programmatic select.
let suppressProgrammaticExpandAfterReload = false;

/** Собрать идентичность табличного элемента из полей request (один переносимый элемент). */
function requestToTableIdentity(arg: AnalyticsRequestData): AnalyticsElementIdentity {
  return {
    ElementId: arg.id,
    Kind: arg.kind ?? (arg.type === BaseItemTypes.SELECTION ? AnalyticsElementKind.Selection : arg.type === BaseItemTypes.PROJECTION ? AnalyticsElementKind.Projection : AnalyticsElementKind.List),
    Source: arg.type === BaseItemTypes.PROJECTION ? arg.sourceType : undefined,
    Destination: arg.destinationType!,
  };
}

/** Собрать идентичность графика из полей request (один переносимый элемент). */
function requestToChartIdentity(arg: AnalyticsRequestData): AnalyticsChartIdentity {
  const chartType = arg.type === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : arg.type === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : AnalyticsV2ChartType.Linear;
  return {
    ChartId: arg.id,
    ChartType: chartType,
    Destination: arg.destinationType!,
  };
}

/** Собрать идентичность папки из полей request (один переносимый элемент). При перемещении папки передавать movedFolderId. */
function requestToFolderIdentity(arg: AnalyticsRequestData): AnalyticsFolderIdentity {
  return {
    FolderId: arg.movedFolderId ?? arg.id,
    Segment: arg.destinationType!,
  };
}

function getTypeSortOrder(elType: number): number {
  switch (elType) {
    case BaseItemTypes.FOLDER: return 0
    case BaseItemTypes.SELECTION: return 1
    case BaseItemTypes.GRAPH:
    case BaseItemTypes.PIE_CHART:
    case BaseItemTypes.BAR_CHART:
      return 2
    case BaseItemTypes.PROJECTION: return 3
    case BaseItemTypes.LIST: return 4
    default: return 5
  }
}

function compareNames(a: string, b: string): number {
  const nameA = (a || '').trim()
  const nameB = (b || '').trim()

  const firstA = nameA.match(/[0-9A-Za-zА-Яа-яЁё]/)?.[0] ?? ''
  const firstB = nameB.match(/[0-9A-Za-zА-Яа-яЁё]/)?.[0] ?? ''

  const group = (ch: string): number => {
    if (/[0-9]/.test(ch)) return 0
    if (/[А-Яа-яЁё]/.test(ch)) return 1
    if (/[A-Za-z]/.test(ch)) return 2
    return 3
  }

  const groupA = group(firstA)
  const groupB = group(firstB)
  if (groupA !== groupB) return groupA - groupB

  return nameA.localeCompare(nameB, 'ru', { numeric: true, sensitivity: 'base' })
}

function sortUnifiedItems(items: any[]): any[] {
  return items.slice().sort((a: any, b: any) => {
    const typeA = getTypeSortOrder(a.elType)
    const typeB = getTypeSortOrder(b.elType)
    if (typeA !== typeB) return typeA - typeB
    return compareNames(a.Name, b.Name)
  })
}

// Индекс, куда вставить node, чтобы массив остался отсортированным по тому же правилу,
// что и sortUnifiedItems (тип через getTypeSortOrder, затем имя через compareNames),
// НЕ пересортировывая остальные элементы. Возвращает первый индекс, перед которым должен встать node.
function sortedInsertIndex(arr: any[], node: any): number {
  const typeNode = getTypeSortOrder(node.elType)
  for (let i = 0; i < arr.length; i++) {
    const cur = arr[i]
    const typeCur = getTypeSortOrder(cur.elType)
    if (typeNode !== typeCur) {
      if (typeNode < typeCur) return i
      continue
    }
    if (compareNames(node.Name, cur.Name) < 0) return i
  }
  return arr.length
}

/**
 * Вычислить elType для дочернего элемента отчёта по уже раскрытым полям.
 * el должен быть результатом mapReportChildElement (с плоскими Id/SrcType/DestType/ChartType/Kind).
 */
export function computeReportChildElType(el: any): number {
  if (el.ElementType == AnalyticsReportElementType.List || el.Kind === AnalyticsElementKind.List) {
    return BaseItemTypes.LIST
  }
  if (el.DestType === undefined) {
    return !el.Id ? BaseItemTypes.BREAK : BaseItemTypes.REPORT
  }
  if (el.ChartType !== undefined) {
    return el.ChartType === 0 ? BaseItemTypes.GRAPH : (el.ChartType === 1 ? BaseItemTypes.PIE_CHART : BaseItemTypes.BAR_CHART)
  }
  if (el.Kind === AnalyticsElementKind.Projection || (el.SrcType !== undefined && el.SrcType !== null)) {
    return BaseItemTypes.PROJECTION
  }
  return BaseItemTypes.SELECTION
}

/**
 * Распаковать дочерний элемент отчёта в плоский объект.
 * Новый контракт сервера: внутри Selection/Projection/List лежит Identity {ElementId, Source, Destination, Kind},
 * внутри Chart — {ChartId, ChartType, Destination}. Раскладываем в плоские Id/SrcType/DestType/ChartType/Kind,
 * чтобы дальнейший код (формула elType, getQueryResult) работал единообразно.
 */
export function mapReportChildElement(el: AnalyticsReportElement): any {
  let res: any = {}
  switch (el.ElementType) {
    case AnalyticsReportElementType.Selection: {
      res = (el as AnalyticsReportLinkToSelection).Selection ?? {}
      const ident: any = res.Identity
      if (ident) {
        res.Id = ident.ElementId
        res.SrcType = ident.Source ?? null
        res.DestType = ident.Destination
        res.Kind = ident.Kind
      }
      res.ElementId = (el as AnalyticsReportLinkToSelection).ElementId
      res.ElementScale = (el as AnalyticsReportLinkToElement).ElementScale
      res.ElementType = (el as AnalyticsReportLinkToSelection).ElementType
      break
    }
    case AnalyticsReportElementType.Projection: {
      res = (el as AnalyticsReportLinkToProjection).Projection ?? {}
      const ident: any = res.Identity
      if (ident) {
        res.Id = ident.ElementId
        res.SrcType = ident.Source ?? null
        res.DestType = ident.Destination
        res.Kind = ident.Kind
      }
      res.ElementId = (el as AnalyticsReportLinkToProjection).ElementId
      res.ElementScale = (el as AnalyticsReportLinkToElement).ElementScale
      res.ElementType = (el as AnalyticsReportLinkToProjection).ElementType
      break
    }
    case AnalyticsReportElementType.List: {
      res = (el as AnalyticsReportLinkToList).List ?? {}
      const ident: any = res.Identity
      if (ident) {
        res.Id = ident.ElementId
        res.SrcType = null
        res.DestType = ident.Destination
        res.Kind = ident.Kind ?? AnalyticsElementKind.List
      }
      res.ElementId = (el as AnalyticsReportLinkToList).ElementId
      res.ElementScale = (el as AnalyticsReportLinkToElement).ElementScale
      res.ElementType = (el as AnalyticsReportLinkToList).ElementType
      break
    }
    case AnalyticsReportElementType.Chart: {
      res = (el as AnalyticsReportLinkToChart).Chart ?? {}
      const ident: any = res.Identity
      if (ident) {
        res.Id = ident.ChartId
        res.DestType = ident.Destination
        res.ChartType = ident.ChartType
      }
      res.ElementId = (el as AnalyticsReportLinkToChart).ElementId
      res.ElementScale = (el as AnalyticsReportLinkToChart).ElementScale
      res.ElementType = (el as AnalyticsReportLinkToChart).ElementType
      break
    }
    case AnalyticsReportElementType.Break:
    default:
      res = {
        Id: 0,
        ElementId: (el as AnalyticsReportBreak).ElementId,
        Name: (el as AnalyticsReportBreak).BreakText && (el as AnalyticsReportBreak).BreakText !== 'null' ?
          (el as AnalyticsReportBreak).BreakText : '',
        Type: BaseItemTypes.BREAK,
        ElementType: (el as AnalyticsReportBreak).ElementType,
        ElementScale: 0,
      }
      break
  }
  return res
}

/**
 * Store containing functions for getting and manipulation 
 * the main selections and projections list (left analytics window panel)
 */
export type GeneralState = {
  currentElement: BaseListItem;
  // parent selection or projection if currentElement is a chart
  currentParentElement: BaseListItem;
  // root reports list
  reportsList: BaseListItem[];
  // unified list (folders + selections + projections + lists)
  unifiedList: BaseListItem[];
  loading: boolean;
  filterMode: boolean;
  favoriteMode: boolean;

  triggerScrollToSelection: boolean;
  // element to be selected right after list is loaded
  selectedAfter: BaseListItem;
  // scroll or not after selection
  scrollAfter: boolean;
  // Одноразовая подсветка элемента при переходе по ссылке (GET-параметры):
  // identity подсвечиваемого элемента; гасится первым кликом мыши.
  linkHighlight: BaseListItem | null;
  precalculatedCount: number;

  accessContainer: AnalyticsAccessContainer;
  // cache for remembering expanded value; restored after full list reload
  rootExpandedCacheR: { Id: number; expanded: boolean }[];
  rootExpandedCacheU: any[];

  // Контроллер текущей загрузки списка (GetList + фоновые GetElements/{id}).
  // Любой повторный вызов getGeneralAnalyticsData отменяет предыдущий.
  listLoadController: AbortController | null;
};

export interface AnalyticsAccessContainer {
  GeneralAccess: boolean; 
  WriteAccess: boolean;
  AccessToWorkload: boolean;
}

export const generalStore = defineStore({
  id: "generalStore",
  state: () =>
    ({
      currentElement: reactive({} as BaseListItem),
      currentParentElement: reactive({} as BaseListItem),
      reportsList: reactive([]),
      unifiedList: reactive([]),
      selectedAfter: reactive({} as BaseListItem),
      scrollAfter: false,
      linkHighlight: null,
      triggerScrollToSelection: false,
      precalculatedCount: 0,

      accessContainer: ({} as AnalyticsAccessContainer),

      rootExpandedCacheR: reactive([]),
      rootExpandedCacheU: reactive([]),
      loading: false,
      filterMode: false,
      favoriteMode: false,

      listLoadController: null,
    } as GeneralState),

  actions: {
    /**
     * checkGenralAccess method. Backend API method for checking if the user has rights to remove entities
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async checkGeneralAccess() {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/General/GetAccess`);
        if (response.status === 200) {

          const { data } = response;
          this.accessContainer = data
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    setSelectedAfter(arg: BaseListItem, scroll: boolean = false) {
      this.selectedAfter = reactive(arg);
      this.setScrollAfter(scroll)
    },

    // Подсветить элемент при переходе по ссылке (однократно). Гасится первым кликом мыши.
    setLinkHighlight(arg: BaseListItem) {
      this.linkHighlight = reactive(arg);
    },

    clearLinkHighlight() {
      if (this.linkHighlight) this.linkHighlight = null;
    },

    setGlobalFilterMode(arg: boolean) {
      this.filterMode = arg
    },

    // Режим избранного глобальный для всех сегментов:
    // восстанавливаем его из localStorage один раз при старте приложения.
    initFavoriteModeFromStorage() {
      if (typeof window === "undefined") return;
      try {
        const raw = window.localStorage.getItem(FAVORITE_MODE_STORAGE_KEY);
        if (raw === "1") this.favoriteMode = true;
        else if (raw === "0") this.favoriteMode = false;
      } catch (e) {
        console.log(JSON.stringify(e));
      }
    },

    /** Применить строку поиска к дереву UnifiedList: заполняет _searchVisible/_searchTokens и флаг _searchAutoExpand.
     *
     * Правила:
     * - Если элемент найден по вхождению (Name содержит строку), он виден, и ВСЕ его дочерние элементы тоже видимы.
     * - Если нашёлся только дочерний элемент, его родитель и все предки тоже видимы и логически «развёрнуты» в поиске.
     * - Если нашёлся только родительский, показываем его и всех детей.
     */
    applyAnalyticsSearchFilter(filter: string) {
      const tokens = tokenizeSearch(filter);
      const hasTerm = tokens.length > 0;
      // ВАЖНО: для токенов используем makeTokenVariants — он отбрасывает раскладочные
      // варианты со знаками препинания. Без этого "аб" → "f," через toEn ловит любую "а"
      // перед запятой в Name (например, "Москва," → "vjcrdf," содержит "f,") и даёт
      // массу false-positive в выдаче поиска.
      const tokenVariantsCache = hasTerm ? tokens.map((t) => makeTokenVariants(t)) : [];

      const markTree = (nodes: BaseListItem[], ancestorMatched: boolean): boolean => {
        let anyMatchInSubtree = false;

        for (const node of nodes) {
          // @ts-ignore
          const childrenArrays: BaseListItem[][] = [];
          for (const arrKey of CHILD_ARRAYS) {
            const arr = (node as any)[arrKey];
            if (arr && arr.length > 0) childrenArrays.push(arr);
          }

          let selfMatched = false;

          if (hasTerm) {
            const name = (node.Name || '').toString();
            const nameCandidates = makeVariants(name);
            selfMatched = matchesTokens(nameCandidates, tokenVariantsCache);
          }

          const effectiveAncestorMatched = ancestorMatched || selfMatched;

          let childHasMatch = false;
          for (const arr of childrenArrays) {
            if (!arr || arr.length === 0) continue;
            if (markTree(arr, effectiveAncestorMatched)) {
              childHasMatch = true;
            }
          }

          // @ts-ignore
          node._searchVisible = !hasTerm || effectiveAncestorMatched || childHasMatch;
          // @ts-ignore
          node._searchTokens = hasTerm ? tokens : null;
          const hasChildren = childrenArrays.length > 0;
          // @ts-ignore
          node._searchSelfMatched = hasTerm && selfMatched;
          // Для узлов с детьми раскрываем только при совпадении в потомках.
          // Если совпал только родитель, оставляем его свёрнутым, но children
          // будут показаны на этапе flattenTree (VirtualAnalyticsList).
          // Для листьев self-match остаётся критерием «раскрытия».
          // @ts-ignore
          node._searchAutoExpand = hasTerm && (hasChildren ? childHasMatch : selfMatched);

          if (selfMatched || childHasMatch) anyMatchInSubtree = true;
        }

        return anyMatchInSubtree;
      };

      // @ts-ignore
      markTree(this.unifiedList || [], false);
      // @ts-ignore
      markTree(this.reportsList || [], false);
    },

    setFavoriteFilterMode(arg: boolean) {
      this.favoriteMode = arg
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(FAVORITE_MODE_STORAGE_KEY, arg ? "1" : "0");
      } catch (e) {
        console.log(JSON.stringify(e));
      }
    },

    setScrollAfter(arg: boolean) {
      this.scrollAfter = arg
    },

    setTriggerScroll(arg: boolean) {
      this.triggerScrollToSelection = arg
    },

    updateCacheUnified() {
      // Сохраняем текущий expanded/selected по полной Identity,
      // чтобы после перезагрузки списка восстановить раскрытия/выделение без потери структуры.
      // @ts-ignore
      this.rootExpandedCacheU = this.unifiedList ? mapRecursive(function a(el: any, parent: any) {
        return {
          Id: el.Id,
          FolderId: el.FolderId,
          SrcType: el.SrcType,
          DestType: el.DestType,
          ChildSelections: el.ChildSelections ? mapRecursive(a, el, el.ChildSelections) : [],
          ChildProjections: el.ChildProjections ? mapRecursive(a, el, el.ChildProjections) : [],
          Charts: el.Charts ? mapRecursive(a, el, el.Charts) : [],
          FolderChildren: el.FolderChildren ? mapRecursive(a, el, el.FolderChildren) : [],
          expanded: el.isExpanded,
          selected: el.isSelected,
          IsFavorite: el.IsFavorite,
          elType: el.elType,
        }
      // @ts-ignore
      }, undefined, this.unifiedList) : []
    },

    /**
     * updateCacheReports() method remembers expanded status of each reports list item
     */
    updateCacheReports() {
      // @ts-ignore
      this.rootExpandedCacheR = this.reportsList ? mapRecursive(function a(el: any, parent: any) {
        return {
          Id: el.Id,
          SrcType: el.SrcType,
          DestType: el.DestType,
          ChildReportElements: el.ChildReportElements ? mapRecursive(a, el, el.ChildReportElements) : [],
          expanded: el.isExpanded,
          selected: el.isSelected,
          // el — уже смаппированный BaseListItem, у него уже есть готовый elType.
          // Если по какой-то причине отсутствует — пересчитываем тем же helper'ом.
          elType: el.elType !== undefined ? el.elType : computeReportChildElType(el),
        }
      // @ts-ignore
      }, undefined, this.reportsList) : []
    },

    /**
     * getGeneralAnalyticsData method. Backend API method for getting base root selections and projections list.
     * @param listType - root store selector mode
     * @returns void if successful, error otherwise
     */
    async getGeneralAnalyticsData(listType: AnalyticsV2ElementType) {
      // const setstore = settingsStore()
      // setstore.stopRequest()

      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)

      // Отменяем предыдущую загрузку списка/детей отчётов (если ещё в полёте) — по тому же
      // паттерну, что и в querystore/auxstore/setstore. Любой повторный вызов сбрасывает прошлую.
      if (this.listLoadController) {
        this.listLoadController.abort()
      }
      const controller = new AbortController()
      this.listLoadController = controller
      const signal = controller.signal

      try {
        if (listType <= 100) {
          this.loading = !this.unifiedList.length;
          const { data } = await http.get(`Analytics/General/GetList?segment=${listType}`, { signal });
          if (signal.aborted) return;
          if (data) {
            this.reportsList = reactive([])
            let selected_el: any = undefined

            const that = this
            // remember cache
            this.updateCacheUnified();

            // Индекс кэша по полной identity:
            // - папка: elType + FolderId
            // - прочие: elType + Id + SrcType + DestType
            // Нужен, чтобы не делать повторные глубокие findRecursive в горячем пути.
            const cacheByFolderId = new Map<number, any>();
            const cacheByElementIdentity = new Map<string, any>();
            const keyPart = (v: any) => (v === undefined ? '__undef__' : (v === null ? '__null__' : String(v)));
            const makeElementIdentityKey = (elType: number, id: any, srcType: any, destType: any) =>
              `${keyPart(elType)}|${keyPart(id)}|${keyPart(srcType)}|${keyPart(destType)}`;
            const indexCacheTree = (nodes: any[]) => {
              if (!nodes?.length) return;
              for (const node of nodes) {
                if (!node) continue;
                if (node.elType === BaseItemTypes.FOLDER && node.FolderId !== undefined && node.FolderId !== null) {
                  cacheByFolderId.set(node.FolderId, node);
                } else {
                  cacheByElementIdentity.set(
                    makeElementIdentityKey(node.elType, node.Id, node.SrcType, node.DestType),
                    node,
                  );
                }
                if (node.FolderChildren?.length) indexCacheTree(node.FolderChildren);
                if (node.ChildSelections?.length) indexCacheTree(node.ChildSelections);
                if (node.ChildProjections?.length) indexCacheTree(node.ChildProjections);
                if (node.Charts?.length) indexCacheTree(node.Charts);
              }
            };
            indexCacheTree(that.rootExpandedCacheU as any[]);

            // Build unified list: folders + root selections + projections + lists
            const mapElementForUnified = function mapEl(el: any, parent: any): any {
              const ident = el.Identity
              const isChart = ident && ident.ChartId !== undefined
              const id = isChart ? ident.ChartId : (ident ? ident.ElementId : el.Id)
              const srcType = isChart ? null : (ident ? ident.Source : el.SrcType)
              const destType = ident ? ident.Destination : el.DestType
              const chartType = isChart ? ident.ChartType : el.ChartType
              // Табличный элемент: Kind различает выборку, проекцию и список (иначе список в папке отображался бы как выборка и совпадал по Id).
              const kind = ident && (ident as any).Kind
              const elType = chartType !== undefined
                ? (chartType === 0 ? 2 : (chartType === 1 ? 3 : 4))
                : (kind === AnalyticsElementKind.List ? BaseItemTypes.LIST : (kind === AnalyticsElementKind.Projection || (srcType !== undefined && srcType !== null) ? BaseItemTypes.PROJECTION : BaseItemTypes.SELECTION))
              // Восстанавливаем expanded/selected только по ПОЛНОЙ Identity:
              // - папка: FolderIdentity (elType + FolderId)
              // - прочие: AnalyticsElementIdentity (elType + Id + SrcType + DestType)
              // Без упрощений и truthy-проверок.
              const cacheItem = cacheByElementIdentity.get(
                makeElementIdentityKey(elType, id, srcType, destType),
              )

              if (cacheItem && cacheItem.selected) {
                selected_el = { Id: id, SrcType: srcType, DestType: destType, IsFavorite: el.IsFavorite, elType }
              }
              const parentName = parent ? parent.parentName + '/' + el.Name : el.Name
              return {
                Id: id,
                Name: el.Name,
                CombinedName: parentName,
                parent: parent?.el,
                SrcType: srcType,
                DestType: destType,
                ChartType: chartType,
                ChildSelections: el.ChildSelections ? mapRecursive(mapEl, { el, parentName }, el.ChildSelections) : [],
                ChildProjections: el.ChildProjections ? mapRecursive(mapEl, { el, parentName }, el.ChildProjections) : [],
                Charts: el.Charts ? mapRecursive(mapEl, { el, parentName }, el.Charts) : [],
                isExpanded: cacheItem ? cacheItem.expanded : false,
                isSelected: cacheItem ? cacheItem.selected : false,
                isEditing: false,
                isShowingMenu: false,
                isProcessing: false,
                isDeleting: false,
                IsFavorite: el.IsFavorite,
                elType,
              }
            }

            const mapFolderForUnified = function mapFolder(folder: any, parent: any): any {
              const folderIdent = folder.Identity
              const folderId = folderIdent ? folderIdent.FolderId : folder.FolderId
              const folderSegment = folderIdent ? folderIdent.Segment : listType

              const cacheItem = cacheByFolderId.get(folderId)

              if (cacheItem && cacheItem.selected) {
                selected_el = { FolderId: folderId, elType: BaseItemTypes.FOLDER, DestType: folderSegment, IsFavorite: folder.IsFavorite }
              }

              const parentCtx = { el: folder, parentName: parent ? parent.parentName + '/' + folder.Name : folder.Name }
              const childFolders = folder.Folders ? folder.Folders.map((f: any) => mapFolder(f, parentCtx)) : []
              const childSelections = folder.Selections ? mapRecursive(mapElementForUnified, parentCtx, folder.Selections) : []
              const childProjections = folder.Projections ? mapRecursive(mapElementForUnified, parentCtx, folder.Projections) : []
              const childLists = folder.AnalyticsLists ? mapRecursive(mapElementForUnified, parentCtx, folder.AnalyticsLists) : []
              const childCharts = folder.Charts ? mapRecursive(mapElementForUnified, parentCtx, folder.Charts) : []

              return {
                Id: 0,
                FolderId: folderId,
                Name: folder.Name,
                CombinedName: parent ? parent.parentName + '/' + folder.Name : folder.Name,
                parent: parent?.el,
                DestType: folderSegment,
                ChildSelections: [],
                ChildProjections: [],
                // У папки один смешанный контейнер детей FolderChildren, отсортированный по типу+имени.
                // Графики тоже кладём сюда (а не в отдельный Charts), иначе при рендере по порядку
                // массивов они оказываются после списков. Charts держим пустым для папок.
                Charts: [],
                FolderChildren: sortUnifiedItems([...childFolders, ...childSelections, ...childCharts, ...childProjections, ...childLists]),
                isExpanded: cacheItem ? cacheItem.expanded : false,
                isSelected: cacheItem ? cacheItem.selected : false,
                isEditing: false,
                isShowingMenu: false,
                isProcessing: false,
                isDeleting: false,
                IsFavorite: folder.IsFavorite ?? false,
                elType: BaseItemTypes.FOLDER,
              }
            }

            const mappedFolders = data.Folders ? data.Folders.map((f: any) => mapFolderForUnified(f, undefined)) : []
            const mappedSelectionsU = mapRecursive(mapElementForUnified, undefined, data.Selections || [])
            const mappedProjectionsU = mapRecursive(mapElementForUnified, undefined, data.Projections || [])
            const mappedListsU = data.AnalyticsLists ? mapRecursive(function a(el: any, parent: any): any {
              const ident = el.Identity
              const isChart = ident && ident.ChartId !== undefined
              const id = isChart ? ident.ChartId : (ident ? ident.ElementId : el.Id)
              const srcType = isChart ? null : (ident ? ident.Source : el.SrcType)
              const destType = ident ? ident.Destination : el.DestType
              const chartType = isChart ? ident.ChartType : el.ChartType
              const kind = ident && (ident as any).Kind
              const elType = parent ? (chartType !== undefined ? (chartType === 0 ? 2 : (chartType === 1 ? 3 : 4)) :
                (kind === AnalyticsElementKind.List ? BaseItemTypes.LIST : (kind === AnalyticsElementKind.Projection || (srcType !== undefined && srcType !== null) ? BaseItemTypes.PROJECTION : BaseItemTypes.SELECTION))) :
                BaseItemTypes.LIST
              const cacheItem = cacheByElementIdentity.get(
                makeElementIdentityKey(elType, id, srcType, destType),
              )

              if (cacheItem && cacheItem.selected) {
                selected_el = { Id: id, SrcType: srcType, DestType: destType, IsFavorite: el.IsFavorite, elType }
              }
              const parentName = parent ? parent.parentName + '/' + el.Name : el.Name
              return {
                Id: id,
                Name: el.Name,
                CombinedName: parentName,
                parent: parent?.el,
                SrcType: srcType,
                DestType: destType,
                ChartType: chartType,
                ChildSelections: el.ChildSelections ? mapRecursive(a, { el, parentName }, el.ChildSelections) : [],
                ChildProjections: el.ChildProjections ? mapRecursive(a, { el, parentName }, el.ChildProjections) : [],
                Charts: el.Charts ? mapRecursive(a, { el, parentName }, el.Charts) : [],
                isExpanded: cacheItem ? cacheItem.expanded : false,
                isSelected: cacheItem ? cacheItem.selected : false,
                isEditing: false,
                isShowingMenu: false,
                isProcessing: false,
                isDeleting: false,
                IsFavorite: el.IsFavorite,
                elType,
              }
            }, undefined, data.AnalyticsLists) : []

            this.unifiedList = sortUnifiedItems([...mappedFolders, ...mappedSelectionsU, ...mappedProjectionsU, ...mappedListsU])
            const selectionSourceForReload: 'user' | 'programmatic' =
              suppressProgrammaticExpandAfterReload ? 'user' : 'programmatic';
            suppressProgrammaticExpandAfterReload = false;

            const hasSelectedAfterIdentity = !!that.selectedAfter && (
              (that.selectedAfter.elType === BaseItemTypes.FOLDER && !!that.selectedAfter.FolderId) ||
              (that.selectedAfter.elType !== BaseItemTypes.FOLDER && !!that.selectedAfter.Id)
            );

            // Переход по ссылке на выборку вне избранного при включённом режиме избранного:
            // снимаем режим, чтобы элемент был виден со всей вложенностью.
            if (that.linkHighlight && that.favoriteMode && hasSelectedAfterIdentity) {
              // Матчинг идентичности повторяет selectElement 1:1 (truthy-проверка SrcType,
              // учёт ElementId), иначе для выборок расходится: SrcType из ссылки может
              // прийти как NaN ("null" в URL), и строгое сравнение не находит узел.
              const matchTarget = (el: any, t: any): boolean => {
                const folderMatch = el.elType === BaseItemTypes.FOLDER && t.elType === BaseItemTypes.FOLDER &&
                  el.FolderId && el.FolderId === t.FolderId;
                const elementMatch = el.elType !== BaseItemTypes.FOLDER &&
                  el.Id === t.Id &&
                  (t.SrcType ? (el.SrcType === t.SrcType) : !el.SrcType) &&
                  el.DestType === t.DestType &&
                  el.elType === t.elType &&
                  ((!el.ElementId && !t.ElementId) || el.ElementId === t.ElementId);
                return !!(folderMatch || elementMatch);
              };
              const target = findRecursive(function a(el: any, t: any): any | undefined {
                if (!el) return undefined;
                if (matchTarget(el, t)) return el;
                for (const key of ['FolderChildren', 'ChildSelections', 'ChildProjections', 'Charts']) {
                  if (el[key]) { const r = findRecursive(a, t, el[key]); if (r) return r; }
                }
                return undefined;
              }, that.selectedAfter, this.unifiedList);
              if (target && !target.IsFavorite) {
                this.setFavoriteFilterMode(false);
              }
            }

            if (hasSelectedAfterIdentity) {
              setTimeout(() => {
                // console.log('selected after: ')
                // console.log(that.selectedAfter)
                that.applyElementAction(that.selectedAfter, 'select', true, false, selectionSourceForReload)
                // that.selectedAfter = reactive({} as BaseListItem);
              }, 10);
            } else if (selected_el) {
              setTimeout(() => {
                // console.log('selected after: ')
                // console.log(selected_el)
                that.applyElementAction(selected_el, 'select', true, false, selectionSourceForReload)
              }, 10);
            }
          }
          this.loading = false
        } else if (listType === AnalyticsV2ElementType.Report) {
          this.unifiedList = reactive([])
          let selected_el: any = undefined

          const that = this
            // remember cache
          this.updateCacheReports();
          // reports data
          this.loading = !this.reportsList.length;
          const { data } = await http.get(`Analytics/Reports/GetList`, { signal });
          if (signal.aborted) return;

          if (data) {
            // Прогрессивная загрузка: сначала отчёты (без детей), затем параллельно — дети.
            // Помогает рекурсивно искать в кэше — найти cache для верхнего уровня и для каждого ребёнка.
            const findInCacheR = function findInCacheR(el_to_find: any): any | undefined {
              return findRecursive(function a(element: any, target: any): any | undefined {
                if (!element) return undefined
                if (element.Id === target.Id &&
                    (target.SrcType ? (element.SrcType === target.SrcType) : !element.SrcType) &&
                    element.DestType === target.DestType &&
                    element.elType === target.elType) {
                  return element
                }
                if (element.ChildReportElements) {
                  return findRecursive(a, target, element.ChildReportElements)
                }
                return undefined
              }, el_to_find, that.rootExpandedCacheR)
            }

            // Шаг 1: рендерим отчёты сразу как placeholder'ы с пустыми детьми и isLoadingChildren=true.
            this.reportsList = data.map((rep: any) => {
              const elTypeTop = computeReportChildElType(rep)
              const cache = findInCacheR({ ...rep, elType: elTypeTop })
              if (cache && cache.selected) {
                selected_el = {
                  Id: rep.Id,
                  ElementId: rep.ElementId,
                  ElementScale: rep.ElementScale,
                  SrcType: rep.SrcType,
                  DestType: rep.DestType,
                  elType: elTypeTop,
                }
              }
              return reactive({
                ElementId: rep.ElementId,
                ElementScale: rep.ElementScale,
                Id: rep.Id,
                Name: rep.Name,
                CombinedName: rep.Name,
                parent: undefined,
                SrcType: rep.SrcType,
                DestType: rep.DestType,
                ChartType: rep.ChartType,
                ChildSelections: [],
                ChildProjections: [],
                Charts: [],
                ChildReportElements: reactive([]),
                isExpanded: cache ? cache.expanded : false,
                isSelected: cache ? cache.selected : false,
                isEditing: false,
                isShowingMenu: false,
                isProcessing: false,
                isDeleting: false,
                isLoadingChildren: !!rep.Id,
                elType: elTypeTop,
              }) as any
            })

            // Шаг 2: применяем selection и завершаем основную загрузку — отчёты уже видны.
            const selectionSourceForReload: 'user' | 'programmatic' =
              suppressProgrammaticExpandAfterReload ? 'user' : 'programmatic';
            suppressProgrammaticExpandAfterReload = false;

            const hasSelectedAfterIdentity = !!that.selectedAfter && (
              (that.selectedAfter.elType === BaseItemTypes.FOLDER && !!that.selectedAfter.FolderId) ||
              (that.selectedAfter.elType !== BaseItemTypes.FOLDER && !!that.selectedAfter.Id)
            );
            if (hasSelectedAfterIdentity) {
              setTimeout(() => {
                that.applyElementAction(that.selectedAfter, 'select', true, false, selectionSourceForReload)
              }, 10);
            } else if (selected_el) {
              setTimeout(() => {
                that.applyElementAction(selected_el, 'select', true, false, selectionSourceForReload)
              }, 10);
            }
            setTimeout(() => {
              that.setSelectedAfter(reactive({} as BaseListItem));
            }, 1000)
            this.loading = false

            // Шаг 3: параллельно подгружаем детей по каждому отчёту (concurrency limit = 4).
            // На каждый ответ — splice в нужный отчёт + снимаем isLoadingChildren.
            const reportsWithIds = data.filter((r: any) => !!r.Id)
            const fetchAndApplyChildren = async (rep: any) => {
              try {
                if (signal.aborted) return
                const elements_data = await http.get(`Analytics/Reports/GetElements/${rep.Id}`, { signal })
                if (signal.aborted) return
                if (!elements_data?.data) return
                const target = (that.reportsList || []).find((r: BaseListItem) => r.Id === rep.Id)
                if (!target) return
                const parentName = target.Name
                const mappedChildren = elements_data.data.map((rawEl: AnalyticsReportElement) => {
                  const childEl = mapReportChildElement(rawEl)
                  const childElType = computeReportChildElType(childEl)
                  const childCache = findInCacheR({ ...childEl, elType: childElType })
                  if (childCache && childCache.selected) {
                    selected_el = {
                      Id: childEl.Id,
                      ElementId: childEl.ElementId,
                      ElementScale: childEl.ElementScale,
                      SrcType: childEl.SrcType,
                      DestType: childEl.DestType,
                      elType: childElType,
                    }
                  }
                  return {
                    ElementId: childEl.ElementId,
                    ElementScale: childEl.ElementScale,
                    Id: childEl.Id,
                    Name: childEl.Name,
                    CombinedName: parentName + '/' + childEl.Name,
                    parent: target,
                    SrcType: childEl.SrcType,
                    DestType: childEl.DestType,
                    ChartType: childEl.ChartType,
                    ChildSelections: [],
                    ChildProjections: [],
                    Charts: [],
                    ChildReportElements: [],
                    isExpanded: childCache ? childCache.expanded : false,
                    isSelected: childCache ? childCache.selected : false,
                    isEditing: false,
                    isShowingMenu: false,
                    isProcessing: false,
                    isDeleting: false,
                    elType: childElType,
                  }
                })
                target.ChildReportElements.splice(0, target.ChildReportElements.length, ...mappedChildren)
                ;(target as any).isLoadingChildren = false
              } catch (e) {
                if ((e as any)?.name === 'CanceledError' || signal.aborted) return
                const target = (that.reportsList || []).find((r: BaseListItem) => r.Id === rep.Id)
                if (target) (target as any).isLoadingChildren = false
                console.log(JSON.stringify(e))
              }
            }

            const CONCURRENCY = 4
            const queue = reportsWithIds.slice()
            const workerCount = Math.min(CONCURRENCY, queue.length)
            const workers: Promise<void>[] = []
            for (let i = 0; i < workerCount; i++) {
              workers.push((async () => {
                while (queue.length > 0) {
                  // Если пользователь уже переключил сегмент — не дёргаем оставшиеся отчёты из очереди.
                  if (signal.aborted) break
                  const rep = queue.shift()
                  if (!rep) break
                  await fetchAndApplyChildren(rep)
                }
              })())
            }
            // Намеренно без await: UI уже отдан, дети подтянутся по мере готовности.
            Promise.allSettled(workers)
          } else {
            this.loading = false
          }
        }
        // Успешно завершили загрузку — освобождаем контроллер, если за время выполнения
        // не появился новый (иначе оставляем актуальный, который уже принадлежит другой загрузке).
        if (this.listLoadController === controller) {
          this.listLoadController = null
        }
      } catch (e) {
        if ((e as any)?.name === 'CanceledError' || signal.aborted) {
          // Тихо выходим: новая загрузка уже в процессе, она корректно перезапишет состояние.
          return
        }
        console.log(JSON.stringify(e))
        this.loading = false
        if (this.listLoadController === controller) {
          this.listLoadController = null
        }
      }
    },

    /**
     * createRootSelection method. Backend API method for creating a new root selection.
     * @param listType - root store selector mode
     * @param pos - position; default 0
     * @returns void if successful, error otherwise
     */
    async createRootSelection(listType: AnalyticsV2ElementType, pos: number = 0, parentFolderId?: number, setFavorite?: boolean) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const effectiveSetFavorite = setFavorite ?? this.favoriteMode
      try {
        if (listType <= 100) {
          const { data } = await http.post(`Analytics/Selections/Create/Root`, {
            SelectionType: listType,
            Position: pos,
            ParentFolderId: parentFolderId ?? null,
            SetFavorite: effectiveSetFavorite
          });
          if (data) {
            const respIdent = data.Identity
            const respId = respIdent ? respIdent.ElementId : data.Id
            const respSrc = respIdent ? respIdent.Source : data.SrcType
            const respDest = respIdent ? respIdent.Destination : data.DestType
            const respKind = respIdent && (respIdent as any).Kind
            const elType = respKind === AnalyticsElementKind.List ? BaseItemTypes.LIST
              : (respKind === AnalyticsElementKind.Projection || (respSrc !== undefined && respSrc !== null) ? BaseItemTypes.PROJECTION : BaseItemTypes.SELECTION)
            this.selectedAfter = reactive({
              Id: respId,
              SrcType: respSrc,
              DestType: respDest,
              elType,
            } as BaseListItem)
            // newElement возвращается сразу, чтобы Analytics.vue
            // мог вызвать insertElementLocally без полной перезагрузки.
            return { response_ok: true, newElement: data }
          }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => this.getGeneralAnalyticsData(rootstore.generalMode), 300)
      }
    },

    /**
     * createChild method. Backend API method for creating a new selection or a projection
     * inside existing selection / projection.
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async createChild(arg: AnalyticsRequestData) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const effectiveSetFavorite = arg.setFavorite ?? this.favoriteMode
      try {
        let http_url = "";
        let params = {}
        // arg.type - what is created (new data)
        // arg.newParentType - existing element to become a parent element
        switch (arg.type) {
          case BaseItemTypes.SELECTION:
            switch (arg.newParentType) {
              // create selection inside selection (...Parent... fields)
              case BaseItemTypes.SELECTION:
                params = {
                  ParentIdentity: {
                    ElementId: arg.newParentId,
                    Kind: AnalyticsElementKind.Selection,
                    Destination: arg.destinationType
                  } as AnalyticsElementIdentity,
                  SetFavorite: effectiveSetFavorite
                }
                http_url = `Analytics/Selections/Create/Child`;
                break;
              // create selection inside list (...Parent... fields)
              case BaseItemTypes.LIST:
                params = {
                  ParentIdentity: {
                    ElementId: arg.newParentId,
                    Kind: AnalyticsElementKind.List,
                    Destination: arg.destinationType
                  } as AnalyticsElementIdentity,
                  SetFavorite: effectiveSetFavorite
                }
                http_url = `Analytics/Selections/Create/Child`;
                break;
              // create selection inside projection (...Parent... fields)
              case BaseItemTypes.PROJECTION:
                params = {
                  ParentIdentity: {
                    ElementId: arg.newParentId,
                    Source: arg.newParentSourceType,
                    Kind: AnalyticsElementKind.Projection,
                    Destination: arg.destinationType
                  } as AnalyticsElementIdentity,
                  SetFavorite: effectiveSetFavorite
                }
                http_url = `Analytics/Selections/Create/Child`;
                break;
              default:
                http_url = ""
                break;
            }
            break;
          // create projection inside selection or projection (...Parent... fields)
          case BaseItemTypes.PROJECTION:
            params = {
              ParentIdentity: {
                ElementId: arg.newParentId,
                Source: arg.newParentType && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(arg.newParentType) ? null : arg.newParentSourceType,
                Kind: arg.newParentType === BaseItemTypes.SELECTION ? AnalyticsElementKind.Selection : 
                  (arg.newParentType === BaseItemTypes.PROJECTION ? AnalyticsElementKind.Projection : AnalyticsElementKind.List),
                Destination: arg.newParentDestinationType
              } as AnalyticsElementIdentity,
              ProjectionDestination: arg.destinationType,
              SetFavorite: effectiveSetFavorite
            }
            http_url = `Analytics/Projections/Create`;
            break;
            // create list based on parent selection or projection (...Parent... fields). SetFavorite везде где есть — зададим в будущем.
          case BaseItemTypes.LIST:
            params = {
              ElementIdentity: {
                Destination: arg.newParentDestinationType,
                ElementId: arg.newParentId,
                Kind: arg.newParentType === BaseItemTypes.SELECTION ? AnalyticsElementKind.Selection :
                  (arg.newParentType === BaseItemTypes.PROJECTION ? AnalyticsElementKind.Projection : AnalyticsElementKind.List),
                Source: arg.newParentType && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(arg.newParentType) ? null : arg.newParentSourceType
              } as AnalyticsElementIdentity,
              SetFavorite: effectiveSetFavorite
            };
            http_url = `Analytics/Lists/Create`;
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const { data } = await http.post(http_url, params);
          return { response_ok: true, newElement: data }
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => this.getGeneralAnalyticsData(rootstore.generalMode), 300)
      }
    },

    /**
     * createChildChart method. Backend API method for creating a new graph, pie chart or a bar chart
     * inside existing selection / projection.
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async createChildChart(arg: AnalyticsRequestData) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const effectiveSetFavorite = arg.setFavorite ?? this.favoriteMode
      try {
        const params = {
          ElementIdentity: {
            ElementId: arg.newParentId,
            Source: arg.newParentType && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(arg.newParentType) ? null : arg.newParentSourceType,
            Destination: arg.destinationType,
            Kind: arg.newParentKind
          } as AnalyticsElementIdentity,
          ChartType: arg.type === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : 
            (arg.type === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : AnalyticsV2ChartType.Linear),
          SetFavorite: effectiveSetFavorite
        }
        const http_url = `Analytics/Charts/Child`;

        const { data } = await http.post(http_url, params);
        if (data) {
          return { response_ok: true, newElement: data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => this.getGeneralAnalyticsData(rootstore.generalMode), 300)
      }
    },

    async createFolder(segment: AnalyticsV2ElementType, parentFolderId?: number, position: number = 0, setFavorite?: boolean) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const effectiveSetFavorite = setFavorite ?? this.favoriteMode
      try {
        const { data } = await http.post(`Analytics/Folders`, {
          Segment: segment,
          ParentFolderId: parentFolderId ?? null,
          DesiredPosition: position,
          SetFavorite: effectiveSetFavorite
        });
        if (data) {
          return { response_ok: true, id: data.Identity ? data.Identity.FolderId : data.Id, newElement: data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => this.getGeneralAnalyticsData(rootstore.generalMode), 300)
      }
    },

    async renameFolder(folderId: number, segment: AnalyticsV2ElementType, newName: string) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        const { data } = await http.patch(`Analytics/Folders/Rename`, {
          FolderIdentity: {
            FolderId: folderId,
            Segment: segment
          } as AnalyticsFolderIdentity,
          NewName: newName
        });
        if (data) {
          return { response_ok: true, name: data.Value }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }
    },

    async deleteFolder(folderId: number, segment: AnalyticsV2ElementType) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const { data } = await http.post(`Analytics/Folders/Remove`, {
          FolderId: folderId,
          Segment: segment
        } as AnalyticsFolderIdentity);
        if (data) return { response_ok: true }
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }
      return { response_ok: false }
    },

    async createRootChart(parentFolderId: number, segment: AnalyticsV2ElementType, chartType: AnalyticsV2ChartType, setFavorite?: boolean) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const effectiveSetFavorite = setFavorite ?? this.favoriteMode
      try {
        const { data } = await http.post(`Analytics/Charts/Root`, {
          ParentFolderId: parentFolderId,
          Segment: segment,
          ChartType: chartType,
          SetFavorite: effectiveSetFavorite
        });
        if (data) {
          return { response_ok: true, newElement: data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => this.getGeneralAnalyticsData(rootstore.generalMode), 300)
      }
    },

    /**
     * insertElementLocally — вставить новый элемент в unifiedList локально после успешного создания.
     * Элемент появляется мгновенно в спозиционированном месте. Полная перезагрузка не вызывается.
     *
     * @param parentIdentity — identity родителя (null для корневого элемента):
     *   для папки: { elType: FOLDER, FolderId }
     *   для остального: { elType, Id, SrcType, DestType }
     *   Сравнение строго по полной identity (как selectElement), без упрощений вроде Id ?? FolderId.
     * @param rawElement — сырые данные из ответа API создания
     * @param position — глобальная позиция среди ВСЕХ потомков на целевом уровне (не внутри конкретного массива).
     *   Пересчитывается в локальную позицию внутри целевого массива с учётом визуального порядка CHILD_ARRAYS_ORDER.
     */
    /**
     * Ищет в дереве исходную выборку и возвращает папку, внутри которой она лежит.
     * Нужно для создания списка: список нельзя положить внутрь выборки, поэтому
     * контейнером для него становится ближайшая папка-предок исходной выборки,
     * а если выборка лежит в корне — возвращаем null (значит, список тоже в корень).
     * Возвращаем именно контейнер (папку/корень), а не саму выборку.
     */
    findNearestFolderIdentity(sourceIdentity: any): { elType: BaseItemTypes; FolderId: number } | null {
      let result: { elType: BaseItemTypes; FolderId: number } | null = null

      const matches = (item: any): boolean => {
        if (sourceIdentity.elType === BaseItemTypes.FOLDER) {
          return item.elType === BaseItemTypes.FOLDER && item.FolderId === sourceIdentity.FolderId
        }
        return item.elType === sourceIdentity.elType &&
          item.Id === sourceIdentity.Id &&
          ((sourceIdentity.SrcType != null) ? item.SrcType === sourceIdentity.SrcType : !item.SrcType) &&
          item.DestType === sourceIdentity.DestType
      }

      // nearestFolder — ближайшая папка над текущим уровнем (null, пока мы в корне)
      const walk = (items: any[], nearestFolder: any | null): boolean => {
        for (const item of items) {
          if (matches(item)) {
            result = nearestFolder
              ? { elType: BaseItemTypes.FOLDER, FolderId: nearestFolder.FolderId }
              : null
            return true
          }
          // при спуске глубже папкой-контейнером становится текущий элемент, если это папка
          const childFolder = item.elType === BaseItemTypes.FOLDER ? item : nearestFolder
          if (item.FolderChildren && walk(item.FolderChildren, childFolder)) return true
          if (item.ChildSelections && walk(item.ChildSelections, childFolder)) return true
          if (item.ChildProjections && walk(item.ChildProjections, childFolder)) return true
          if (item.Charts && walk(item.Charts, childFolder)) return true
        }
        return false
      }

      walk(this.unifiedList, null)
      return result
    },

    insertElementLocally(parentIdentity: any | null, rawElement: any, position: number) {
      const ident = rawElement.Identity
      const isChart = ident && ident.ChartId !== undefined
      const isFolder = ident && ident.FolderId !== undefined && !ident.ElementId && !ident.ChartId

      let newItem: any

      if (isFolder) {
        const folderId = ident.FolderId
        const segment = ident.Segment
        newItem = {
          Id: 0,
          FolderId: folderId,
          Name: rawElement.Name ?? '',
          CombinedName: rawElement.Name ?? '',
          DestType: segment,
          ChildSelections: [],
          ChildProjections: [],
          Charts: [],
          FolderChildren: [],
          isExpanded: false,
          isSelected: false,
          isEditing: true,
          isShowingMenu: false,
          isProcessing: false,
          isDeleting: false,
          IsFavorite: rawElement.IsFavorite ?? this.favoriteMode,
          elType: BaseItemTypes.FOLDER,
        }
      } else {
        const id = isChart ? ident.ChartId : (ident ? ident.ElementId : rawElement.Id)
        const srcType = isChart ? null : (ident ? ident.Source : rawElement.SrcType)
        const destType = ident ? ident.Destination : rawElement.DestType
        const chartType = isChart ? ident.ChartType : rawElement.ChartType
        const kind = ident && (ident as any).Kind
        const elType = chartType !== undefined
          ? (chartType === 0 ? 2 : (chartType === 1 ? 3 : 4))
          : (kind === AnalyticsElementKind.List ? BaseItemTypes.LIST
            : (kind === AnalyticsElementKind.Projection || (srcType !== undefined && srcType !== null)
              ? BaseItemTypes.PROJECTION : BaseItemTypes.SELECTION))

        newItem = {
          Id: id,
          Name: rawElement.Name ?? '',
          CombinedName: rawElement.Name ?? '',
          SrcType: srcType,
          DestType: destType,
          ChartType: chartType,
          ChildSelections: [],
          ChildProjections: [],
          Charts: [],
          isExpanded: false,
          isSelected: false,
          isEditing: true,
          isShowingMenu: false,
          isProcessing: false,
          isDeleting: false,
          IsFavorite: rawElement.IsFavorite ?? this.favoriteMode,
          elType,
        }
      }

      if (parentIdentity === null) {
        // Корневой элемент — вставляем напрямую в unifiedList
        const idx = Math.min(position, this.unifiedList.length)
        const item = reactive(newItem)
        // Анимация вставки: сначала включаем CSS transition на строках виртуального списка,
        // затем ждём nextTick (Vue применит класс в DOM) + requestAnimationFrame (браузер отрисует кадр
        // с transition), и только потом делаем splice — виртуализатор пересчитает translateY,
        // и CSS transition плавно сдвинет соседние строки.
        insertAnimActive.value = true
        nextTick(() => {
          requestAnimationFrame(() => {
            this.unifiedList.splice(idx, 0, item)
            // Снимаем флаг после завершения анимации, чтобы при обычном скролле transition не применялся
            setTimeout(() => { insertAnimActive.value = false }, 400)
          })
        })
        return
      }

      const findParent = (items: any[]): any | null => {
        for (const item of items) {
          if (parentIdentity.elType === BaseItemTypes.FOLDER) {
            if (item.elType === BaseItemTypes.FOLDER && item.FolderId === parentIdentity.FolderId) return item
          } else {
            if (item.elType === parentIdentity.elType &&
              item.Id === parentIdentity.Id &&
              ((parentIdentity.SrcType != null) ? item.SrcType === parentIdentity.SrcType : !item.SrcType) &&
              item.DestType === parentIdentity.DestType) return item
          }
          if (item.FolderChildren) { const r = findParent(item.FolderChildren); if (r) return r }
          if (item.ChildSelections) { const r = findParent(item.ChildSelections); if (r) return r }
          if (item.ChildProjections) { const r = findParent(item.ChildProjections); if (r) return r }
          if (item.Charts) { const r = findParent(item.Charts); if (r) return r }
        }
        return null
      }

      const parent = findParent(this.unifiedList)
      if (!parent) {
        console.warn('[insertElementLocally] parent not found', parentIdentity)
        return
      }

      parent.isExpanded = true

      // Визуальный порядок дочерних массивов (совпадает с CHILD_ARRAYS в flattenTree).
      // Элемент вставляется в тот массив, куда попадает глобальная позиция, а НЕ в массив своего типа.
      // Это позволяет визуально разместить элемент точно в позиции, рассчитанной по вьюпорту,
      // даже если он окажется в «чужом» массиве. После полного обновления (~1с) элемент
      // переедет в правильный массив по серверной сортировке.
      const CHILD_ARRAYS_ORDER = ['FolderChildren', 'ChildSelections', 'Charts', 'ChildProjections', 'ChildReportElements'] as const

      // Пересчёт глобальной позиции (среди всех потомков) в локальную позицию внутри конкретного массива:
      // идём по массивам в визуальном порядке, вычитая длину каждого. Когда remaining < arr.length —
      // значит позиция попадает в этот массив. Оператор < (а не <=) гарантирует, что граничная позиция
      // (remaining === arr.length) провалится в следующий массив, а не встанет в конец текущего.
      let remaining = position
      let targetArr: any[] | null = null
      let localPos = 0
      let lastNonEmptyArr: any[] | null = null

      for (const arrKey of CHILD_ARRAYS_ORDER) {
        const arr = parent[arrKey]
        if (!arr) continue
        if (arr.length > 0) lastNonEmptyArr = arr
        if (remaining < arr.length) {
          targetArr = arr
          localPos = remaining
          break
        }
        remaining -= arr.length
      }

      // Если глобальная позиция за пределами всех массивов — вставить в конец последнего непустого
      if (!targetArr) {
        if (lastNonEmptyArr) {
          targetArr = lastNonEmptyArr
          localPos = targetArr.length
        } else {
          targetArr = parent.FolderChildren ?? parent.ChildSelections ?? parent.Charts ?? parent.ChildProjections
          localPos = 0
        }
      }

      if (!targetArr) {
        console.warn('[insertElementLocally] target array not found on parent', parent.elType, newItem.elType)
        return
      }

      console.log('[insertElementLocally]', {
        globalPosition: position,
        localPos,
        parentElType: parent.elType,
        newElType: newItem.elType,
        arrSizes: CHILD_ARRAYS_ORDER.map(k => `${k}:${(parent[k] || []).length}`),
      })

      const item = reactive(newItem)
      const arr = targetArr!
      // Анимация: insertAnimActive включает CSS transition на строках виртуального списка.
      // nextTick ждёт, пока Vue обновит DOM (добавит класс с transition).
      // requestAnimationFrame ждёт, пока браузер отрисует кадр с этим классом.
      // Только после этого splice меняет данные → виртуализатор пересчитывает translateY →
      // CSS transition плавно анимирует сдвиг соседних строк.
      // Без этой двухфазной задержки класс и новые позиции попали бы в один кадр — перехода не будет.
      insertAnimActive.value = true
      nextTick(() => {
        requestAnimationFrame(() => {
          arr.splice(localPos, 0, item)
          setTimeout(() => { insertAnimActive.value = false }, 400)
        })
      })
    },

    /**
     * Локальный batch-перенос для DnD: удаляет элементы из старых мест и вставляет каждый на свою
     * отсортированную позицию ВНУТРИ целевого контейнера. Остальное дерево (в т.ч. unifiedList) не
     * пересортировывается — общий серверный порядок восстанавливается при следующем getGeneralAnalyticsData.
     * Используется только для мгновенного UI-эффекта.
     */
    moveElementsLocallySorted(items: any[], hoverParams: any, moveTargetKind: MoveTargetKind) {
      if (!items?.length || !this.unifiedList?.length) return;

      const normalizeOptional = (v: any) => (v === null ? undefined : v);
      const toIdentityKey = (p: any) => {
        const elType = p.elType ?? '';
        if (elType === BaseItemTypes.FOLDER) {
          const folderId = p.folderId ?? p.FolderId ?? '';
          const dest = p.destinationDataType ?? p.DestType ?? '';
          // Для папки сравниваем FolderIdentity + Segment (без Id=0).
          return `${elType}:folder:${folderId}:${dest}`;
        }
        const id = p.id ?? p.Id ?? '';
        const src = normalizeOptional(p.sourceDataType ?? p.SrcType) ?? '';
        const dest = p.destinationDataType ?? p.DestType ?? '';
        // Для непапок — полная AnalyticsElementIdentity.
        return `${elType}:item:${id}:${src}:${dest}`;
      };

      const moveKeys = new Set(items.map(toIdentityKey));

      const findTargetParent = (itemsTree: any[]): any | null => {
        if (moveTargetKind === 'root') return null;
        const target = hoverParams ?? {};
        const walk = (arr: any[]): any | null => {
          for (const it of arr) {
            if (moveTargetKind === 'folder') {
              if (target.elType === BaseItemTypes.FOLDER &&
                it.elType === BaseItemTypes.FOLDER &&
                it.FolderId === target.folderId &&
                it.DestType === target.destinationDataType) return it;
            } else if (moveTargetKind === 'table') {
              if (it.elType === target.elType &&
                it.Id === target.id &&
                normalizeOptional(it.SrcType) === normalizeOptional(target.sourceDataType) &&
                it.DestType === target.destinationDataType) return it;
            }
            for (const key of CHILD_ARRAYS) {
              const childArr = it[key];
              if (!childArr?.length) continue;
              const found = walk(childArr);
              if (found) return found;
            }
          }
          return null;
        };
        return walk(itemsTree);
      };

      const targetParent = findTargetParent(this.unifiedList);
      if (moveTargetKind !== 'root' && !targetParent) return;

      const extracted: any[] = [];
      const extractFrom = (arr: any[]) => {
        for (let i = arr.length - 1; i >= 0; i--) {
          const node = arr[i];
          const nodeKey = toIdentityKey(node);
          if (moveKeys.has(nodeKey)) {
            extracted.push(arr.splice(i, 1)[0]);
            continue;
          }
          for (const key of CHILD_ARRAYS) {
            const childArr = node[key];
            if (childArr?.length) extractFrom(childArr);
          }
        }
      };
      extractFrom(this.unifiedList);
      if (!extracted.length) return;

      const bucketForTarget = (parent: any | null, node: any): any[] => {
        if (!parent) return this.unifiedList;
        if (moveTargetKind === 'folder') {
          // У папки один смешанный контейнер FolderChildren (включая графики), как и при загрузке с сервера.
          parent.FolderChildren = parent.FolderChildren ?? [];
          return parent.FolderChildren;
        }
        if (node.elType === BaseItemTypes.PROJECTION) {
          parent.ChildProjections = parent.ChildProjections ?? [];
          return parent.ChildProjections;
        }
        if (node.elType === BaseItemTypes.GRAPH || node.elType === BaseItemTypes.PIE_CHART || node.elType === BaseItemTypes.BAR_CHART) {
          parent.Charts = parent.Charts ?? [];
          return parent.Charts;
        }
        parent.ChildSelections = parent.ChildSelections ?? [];
        return parent.ChildSelections;
      };

      // Точечное раскрытие target сохраняем как обычное ручное раскрытие.
      if (targetParent && !targetParent.isExpanded) {
        targetParent.isExpanded = true;
      }

      insertAnimActive.value = true;
      nextTick(() => {
        requestAnimationFrame(() => {
          // Перемещаемый встаёт на свою отсортированную позицию ВНУТРИ целевого массива.
          // Остальные элементы и порядок unifiedList не трогаем: общая пересортировка дерева
          // происходит только после обновления с сервера (getGeneralAnalyticsData). Это убирает
          // «скачки» всего дерева при перемещении.
          for (const node of extracted) {
            const bucket = bucketForTarget(targetParent, node);
            const idx = sortedInsertIndex(bucket, node);
            bucket.splice(idx, 0, node);
          }

          // Фиксируем раскрытие target в кэше, чтобы после delayed refresh осталось как "ручное".
          this.updateCacheUnified();
          setTimeout(() => { insertAnimActive.value = false }, 400);
        });
      });
    },

    moveElementLocallySorted(item: any, hoverParams: any, moveTargetKind: MoveTargetKind) {
      this.moveElementsLocallySorted([item], hoverParams, moveTargetKind);
    },

    /**
     * removeElementLocally — удалить элемент из unifiedList локально с анимацией (до ответа сервера).
     * Вызывается при удалении элемента пользователем: соседние строки плавно съезжаются.
     * Через ~1с полное обновление (getGeneralAnalyticsData) синхронизирует дерево с сервером.
     *
     * Поиск по полной identity (elType + Id + SrcType + DestType для элементов,
     * elType + FolderId для папок), без упрощений.
     */
    removeElementLocally(elIdentity: any) {
      const CHILD_ARRAYS_ORDER = ['FolderChildren', 'ChildSelections', 'Charts', 'ChildProjections', 'ChildReportElements'] as const

      const matchEl = (item: any): boolean => {
        if (elIdentity.elType === BaseItemTypes.FOLDER) {
          return item.elType === BaseItemTypes.FOLDER && item.FolderId === elIdentity.FolderId
        }
        return item.elType === elIdentity.elType &&
          item.Id === elIdentity.Id &&
          ((elIdentity.SrcType != null) ? item.SrcType === elIdentity.SrcType : !item.SrcType) &&
          item.DestType === elIdentity.DestType
      }

      // Рекурсивный поиск элемента во всех дочерних массивах дерева.
      // При нахождении — удаляем с анимацией (та же двухфазная схема: nextTick + rAF, см. insertElementLocally).
      //
      // Важно: индекс НЕ захватываем — храним только ссылку на target. При массовом удалении
      // соседних элементов несколько вызовов removeElementLocally подряд кладут несколько splice'ов
      // в один rAF, и индексы предыдущих захваченных удалений сдвигают массив. Поэтому индекс
      // пересчитывается через arr.indexOf(target) непосредственно перед splice.
      const findAndRemove = (arrays: { arr: any[], key: string }[], parentItem?: any): boolean => {
        for (const { arr } of arrays) {
          const idx = arr.findIndex(matchEl)
          if (idx >= 0) {
            const target = arr[idx]
            insertAnimActive.value = true
            nextTick(() => {
              requestAnimationFrame(() => {
                const currentIdx = arr.indexOf(target)
                if (currentIdx >= 0) {
                  arr.splice(currentIdx, 1)
                  // Если у родителя не осталось детей — сворачиваем
                  if (parentItem) {
                    const total = CHILD_ARRAYS_ORDER.reduce((n, k) => n + ((parentItem[k]?.length) || 0), 0)
                    if (total === 0) parentItem.isExpanded = false
                  }
                }
                setTimeout(() => { insertAnimActive.value = false }, 400)
              })
            })
            return true
          }
          for (const item of arr) {
            const childArrays = CHILD_ARRAYS_ORDER
              .map(k => ({ arr: item[k], key: k }))
              .filter(a => a.arr && a.arr.length > 0)
            if (findAndRemove(childArrays, item)) return true
          }
        }
        return false
      }

      findAndRemove([{ arr: this.unifiedList, key: 'root' }])
    },

    /**
     * changePosition: repositioning root items. Selections/Projections/Lists/Charts ChangePosition
     * APIs disabled in 13.5.2.1 (alphabetical sorting). Only Reports still call API; others just refresh list.
     */
    async changePosition(arg: AnalyticsRequestData): Promise<{ ok: boolean; error?: any }> {
      const rootstore = rootStore()
      const isDisabled =
        arg.type === BaseItemTypes.SELECTION ||
        arg.type === BaseItemTypes.LIST ||
        arg.type === BaseItemTypes.PROJECTION ||
        arg.type === BaseItemTypes.GRAPH ||
        arg.type === BaseItemTypes.PIE_CHART ||
        arg.type === BaseItemTypes.BAR_CHART

      if (isDisabled) return { ok: true }

      // Перед запросом сразу выравниваем локальный порядок под финальный newPosition.
      // Дальнейшая блокировка перетаскиваемого элемента выполняется в moveStoreItem.
      if (arg.type === BaseItemTypes.REPORT && arg.newPosition != null && arg.id != null) {
        const arr = this.reportsList
        if (arr && arr.length > 0) {
          const currentIndex = arr.findIndex((r: BaseListItem) => r.Id === arg.id)
          if (currentIndex !== -1) {
            const target = Math.max(0, Math.min(arg.newPosition, arr.length - 1))
            if (currentIndex !== target) {
              const [item] = arr.splice(currentIndex, 1)
              arr.splice(target, 0, item)
            }
          }
        }
      }

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        let http_url = ""
        let params: Record<string, unknown> = {}
        switch (arg.type) {
          case BaseItemTypes.REPORT:
            http_url = `Analytics/Reports/ChangePosition`
            params = { ReportId: arg.id, DesiredPosition: arg.newPosition }
            break
          default:
            http_url = ""
            break
        }
        if (http_url) {
          await http.put(http_url, params)
        }
        // Успех — ничего не делаем, локальный порядок уже актуальный.
        return { ok: true }
      } catch (e) {
        console.log(JSON.stringify(e))
        // Ошибка — перезапрашиваем весь лист, чтобы синхронизироваться с сервером.
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
        return { ok: false, error: e }
      }
    },

    /**
     * changeReportElementPosition method. Backend API method for repositioning a child report element
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async changeReportElementPosition(arg: AnalyticsRequestData): Promise<{ ok: boolean; error?: any }> {
      console.log('change rep el position:')
      console.log(JSON.stringify(arg))
      const rootstore = rootStore()

      // Перед запросом сразу выравниваем локальный порядок под финальный newPosition.
      // Дальнейшая блокировка перетаскиваемого элемента выполняется в moveStoreItem.
      if (arg.oldParentId != null && arg.newPosition != null && arg.id != null) {
        const parent = (this.reportsList || []).find((r: BaseListItem) => r.Id === arg.oldParentId)
        const arr = parent?.ChildReportElements
        if (arr && arr.length > 0) {
          const currentIndex = arr.findIndex((c: any) => c.ElementId === arg.id)
          if (currentIndex !== -1) {
            const target = Math.max(0, Math.min(arg.newPosition, arr.length - 1))
            if (currentIndex !== target) {
              const [item] = arr.splice(currentIndex, 1)
              arr.splice(target, 0, item)
            }
          }
        }
      }

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        const http_url = `Analytics/Reports/ChangeElementPosition`;
        const params = {
          ElementId: arg.id,
          ElementType: arg.type === BaseItemTypes.BREAK ? 1 : 0,
          DesiredPosition: arg.newPosition
        }

        const response = await http.put(http_url, params);
        console.log(response)

        // Успех — ничего не делаем, локальный порядок уже актуальный.
        return { ok: true }
      } catch (e) {
        console.log(JSON.stringify(e))
        // Ошибка — перезапрашиваем весь лист, чтобы синхронизироваться с сервером.
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
        return { ok: false, error: e }
      }
    },

    /**
     * deleteReportElement method. Backend API method for deleting a child report element
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async deleteReportElement(arg: AnalyticsRequestData) {
      const rootstore = rootStore()

      // Оптимистично удаляем дочерний элемент сразу из локального состояния:
      // ищем во всех отчётах ребёнка с таким ElementId и сплайсим. Полный refetch — только на ошибке.
      if (arg.id != null) {
        for (const report of (this.reportsList || [])) {
          const arr = report.ChildReportElements
          if (!arr || !arr.length) continue
          const idx = arr.findIndex((c: any) => c.ElementId === arg.id)
          if (idx !== -1) {
            arr.splice(idx, 1)
            break
          }
        }
      }

      // Если выбранным был именно этот дочерний элемент отчёта — сбрасываем выделение
      // как при ручном отщёлкивании, чтобы дальнейшие действия не шли по удалённой сущности.
      // splice выше синхронный, поэтому проверка по дереву уже корректна.
      this.resetSelectionIfMissing();

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        const http_url = `Analytics/Reports/RemoveElement`;
        const params = {
          ElementId: arg.id,
          ElementType: arg.type === BaseItemTypes.BREAK ? 1 : 0
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }
        const response = await http.post(http_url, params);
        console.log(response)
        // Успех — ничего не делаем, элемент уже удалён локально.
      } catch (e) {
        console.log(JSON.stringify(e))
        // Ошибка — перезапрашиваем весь лист, чтобы синхронизироваться с сервером.
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }
    },

    /**
     * addReportBreak method. Backend API method for deleting a child report element
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async addReportBreak(arg: AnalyticsRequestData) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = `Analytics/Reports/AddBreak`;
        const params = {
          ReportId: arg.id,
          BreakPosition: arg.newPosition
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }
        const response: any = await http.post(http_url, params);
        const payload = response?.data ?? response;
        const elementId = payload?.ElementId;
        const elementType = payload?.ElementType;
        const breakText = payload?.BreakText;

        // Оптимистичное локальное добавление break-а: целевой отчёт находим по Id,
        // вставляем сконструированный элемент на нужную позицию без полного refetch.
        const targetReport = (this.reportsList || []).find((r: BaseListItem) => r.Id === arg.id);
        if (targetReport && elementId !== undefined && elementId !== null) {
          const newBreak: any = {
            Id: 0,
            ElementId: elementId,
            ElementScale: 0,
            ElementType: elementType,
            Name: breakText && breakText !== 'null' ? breakText : '',
            CombinedName: targetReport.Name,
            parent: targetReport,
            SrcType: undefined,
            DestType: undefined,
            ChartType: undefined,
            ChildSelections: [],
            ChildProjections: [],
            Charts: [],
            ChildReportElements: [],
            isExpanded: false,
            isSelected: false,
            isEditing: false,
            isShowingMenu: false,
            isProcessing: false,
            isDeleting: false,
            elType: BaseItemTypes.BREAK,
          };
          if (!targetReport.ChildReportElements) {
            targetReport.ChildReportElements = reactive([]) as any;
          }
          const insertAt = typeof arg.newPosition === 'number'
            ? Math.max(0, Math.min(arg.newPosition, targetReport.ChildReportElements.length))
            : targetReport.ChildReportElements.length;
          targetReport.ChildReportElements.splice(insertAt, 0, newBreak);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        // Только при ошибке — fallback на полный refetch, чтобы UI и сервер сошлись.
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }
    },

    /**
     * addReportBreak method. Backend API method for deleting a child report element
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async updateElementScale(arg: any) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = `Analytics/Reports/ResizeElement`;
        const params = {
          LinkId: arg.id,
          NewScale: arg.NewScale
        }
          
        if (!http_url) {
          throw new Error('Incorrect API URL');
        }
        const response = await http.put(http_url, params);

        if (response.status === 200 || response.status === 201) {

          const found_rep_el = findRecursive(function a(element: any, el_to_find: any): any | undefined {
            if (!element) return undefined
            
            if (element.ElementId === el_to_find.ElementId &&
                  element.elType === el_to_find.elType) {
              return element;
            }
            if (element.ChildReportElements) {
              return findRecursive(a, el_to_find, element.ChildReportElements)
            }
            return undefined
          }, {
            elType: arg.type,
            ElementId: arg.id,
          } , this.reportsList)

          if (found_rep_el) {
            found_rep_el.ElementScale = arg.NewScale
          }

          console.log('updated element: ')
          console.log(found_rep_el)
        }

        console.log(response)
      } catch (e) {
        console.log(JSON.stringify(e))

        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }
    },

    /**
     * precalcCurrentItem method. Backend API method for the item precalculation that is needed before the list creation
     * @returns object with response_ok if successful, error otherwise
     */
    async precalcCurrentItem(arg: AnalyticsElementIdentity) {
      this.precalculatedCount = 0;

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = `Analytics/Lists/PrecalculateParent`;
          
        if (!http_url) {
          throw new Error('Incorrect API URL');
        }
        const { data } = await http.put(http_url, arg);

        if (data) {
          this.precalculatedCount = data.ElementsCount;
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }
    },

    /**
     * addReportBreak method. Backend API method for deleting a child report element
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async changeReportBreak(arg: AnalyticsRequestData) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = `Analytics/Reports/EditBreak`;
        const params = {
          BreakId: arg.id,
          NewText: arg.NewName
        }
        // Локальное переименование уже выполнено вызывающей стороной (renameElement).
        // На успехе сервера — ничего не обновляем; на ошибке — пересинхронизация с сервером.
        const response = await http.put(http_url, params);
        console.log(response)
        if (response.status === 200 || response.status === 201) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))

        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }
      return { response_ok: false }
    },

    /**
     * Перемещение элементов в корень сегмента. PUT api/Analytics/General/move-to-root-list
     */
    async moveAnalyticsToRootList(request: AnalyticsElementsToRootListMoveRequest) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const res = await http.put('Analytics/General/move-to-root-list', request)
      if (!(res?.status === 200 || res?.status === 201)) throw res
    },

    /**
     * Перемещение элементов в папку. PUT api/Analytics/General/move-to-folder
     */
    async moveAnalyticsToFolder(request: AnalyticsElementsToFolderMoveRequest) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const res = await http.put('Analytics/General/move-to-folder', request)
      if (!(res?.status === 200 || res?.status === 201)) throw res
    },

    /**
     * Перемещение элементов к новому родительскому табличному элементу. PUT api/Analytics/General/move-to-new-parent
     */
    async moveAnalyticsToNewParent(request: AnalyticsElementsToNewParentMoveRequest) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      const res = await http.put('Analytics/General/move-to-new-parent', request)
      if (!(res?.status === 200 || res?.status === 201)) throw res
    },

    /**
     * changeChildParent — маршрутизация перемещения на три API: move-to-root-list, move-to-folder, move-to-new-parent.
     * Важно: isRoot в arg — «буквально корневой» (элемент без родителя). «Корневая выборка по смыслу» — выборка,
     * у которой родитель корень или папка (isRoot || parentType === FOLDER); её запрещено перемещать внутрь другой выборки.
     * @param arg — параметры переносимого элемента и цели (в т.ч. moveTargetKind, targetFolderId, targetSegment, parentType).
     * @returns при недопустимом действии — { errorMessage } для показа уведомления; иначе после успеха — refresh списка.
     */
    async changeChildParent(arg: AnalyticsRequestData): Promise<{ errorMessage?: string } | void> {
      const rootstore = rootStore()

      // Запрет: корневая выборка по смыслу → внутрь другой выборки (не меняем семантику isRoot, проверка только здесь).
      const isRootSelectionByMeaning = arg.type === BaseItemTypes.SELECTION &&
        (arg.isRoot === true || arg.parentType === BaseItemTypes.FOLDER)
      if (isRootSelectionByMeaning && arg.newParentType === BaseItemTypes.SELECTION) {
        return { errorMessage: 'Корневую выборку нельзя переместить внутрь другой выборки' }
      }

      this.applyElementAction({
        Id: arg.newParentType === BaseItemTypes.FOLDER ? undefined : arg.newParentId,
        FolderId: arg.newParentType === BaseItemTypes.FOLDER ? arg.targetFolderId : undefined,
        elType: arg.newParentType,
        SrcType: arg.newParentSourceType,
        DestType: arg.newParentDestinationType
      }, 'start-process')

      const tableIdentity = (): AnalyticsElementIdentity => requestToTableIdentity(arg)
      const chartIdentity = (): AnalyticsChartIdentity => requestToChartIdentity(arg)
      const folderIdentity = (): AnalyticsFolderIdentity => requestToFolderIdentity(arg)

      const targetKind: MoveTargetKind = arg.moveTargetKind ?? (arg.newParentType === BaseItemTypes.FOLDER ? 'folder' : 'table')
      const localHoverParams = {
        id: arg.newParentId,
        folderId: arg.targetFolderId,
        elType: arg.newParentType,
        sourceDataType: arg.newParentSourceType,
        destinationDataType: arg.newParentDestinationType ?? arg.targetSegment,
      };
      const localDraggedItem = {
        id: arg.type === BaseItemTypes.FOLDER ? undefined : arg.id,
        folderId: arg.type === BaseItemTypes.FOLDER ? arg.movedFolderId : undefined,
        elType: arg.type,
        sourceDataType: arg.sourceType,
        destinationDataType: arg.destinationType,
      };

      this.moveElementLocallySorted(localDraggedItem, localHoverParams, targetKind);

      try {
        if (targetKind === 'root') {
          const req: AnalyticsElementsToRootListMoveRequest = { TableElements: [], Charts: [], Folders: [] }
          if (arg.type === BaseItemTypes.SELECTION || arg.type === BaseItemTypes.PROJECTION || arg.type === BaseItemTypes.LIST) {
            req.TableElements = [tableIdentity()]
          } else if (arg.type === BaseItemTypes.GRAPH || arg.type === BaseItemTypes.PIE_CHART || arg.type === BaseItemTypes.BAR_CHART) {
            req.Charts = [chartIdentity()]
          } else if (arg.type === BaseItemTypes.FOLDER) {
            req.Folders = [folderIdentity()]
          }
          await this.moveAnalyticsToRootList(req)
        } else if (targetKind === 'folder' && arg.targetFolderId != null && arg.targetSegment != null) {
          const req: AnalyticsElementsToFolderMoveRequest = {
            TableElements: [], Charts: [], Folders: [],
            TargetFolder: { FolderId: arg.targetFolderId, Segment: arg.targetSegment }
          }
          if (arg.type === BaseItemTypes.SELECTION || arg.type === BaseItemTypes.PROJECTION || arg.type === BaseItemTypes.LIST) {
            req.TableElements = [tableIdentity()]
          } else if (arg.type === BaseItemTypes.GRAPH || arg.type === BaseItemTypes.PIE_CHART || arg.type === BaseItemTypes.BAR_CHART) {
            req.Charts = [chartIdentity()]
          } else if (arg.type === BaseItemTypes.FOLDER) {
            req.Folders = [folderIdentity()]
          }
          await this.moveAnalyticsToFolder(req)
        } else {
          // table: цель — табличный элемент (выборка/проекция/список); Kind задаём явно по newParentKind или по newParentType.
          const targetKind = (arg.newParentKind !== undefined && arg.newParentKind !== null)
            ? arg.newParentKind
            : (arg.newParentType === BaseItemTypes.SELECTION ? AnalyticsElementKind.Selection
              : arg.newParentType === BaseItemTypes.PROJECTION ? AnalyticsElementKind.Projection
                : arg.newParentType === BaseItemTypes.LIST ? AnalyticsElementKind.List
                  : AnalyticsElementKind.Selection)
          const targetParent: AnalyticsElementIdentity = {
            ElementId: arg.newParentId!,
            Kind: targetKind,
            Source: arg.newParentType === BaseItemTypes.PROJECTION ? arg.newParentSourceType : undefined,
            Destination: arg.newParentDestinationType!
          }
          const req: AnalyticsElementsToNewParentMoveRequest = {
            TableElements: [], Charts: [], Folders: [],
            TargetParentElement: targetParent
          }
          if (arg.type === BaseItemTypes.SELECTION || arg.type === BaseItemTypes.PROJECTION || arg.type === BaseItemTypes.LIST) {
            req.TableElements = [tableIdentity()]
          } else if (arg.type === BaseItemTypes.GRAPH || arg.type === BaseItemTypes.PIE_CHART || arg.type === BaseItemTypes.BAR_CHART) {
            req.Charts = [chartIdentity()]
          } else if (arg.type === BaseItemTypes.FOLDER) {
            req.Folders = [folderIdentity()]
          }
          await this.moveAnalyticsToNewParent(req)
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => this.getGeneralAnalyticsData(rootstore.generalMode), 300)
        return
      }
    },

    /**
     * changeChildrenParent — массовое перемещение нескольких элементов.
     * Группирует элементы по типам (table/chart/folder) и отправляет один запрос
     * на нужный endpoint (move-to-root-list / move-to-folder / move-to-new-parent).
     *
     * @param items — массив реально выбранных элементов в формате callbackParams
     *   (elType, id, folderId, sourceDataType, destinationDataType, isRoot, parent_type)
     * @param hoverParams — параметры цели (куда тащим):
     *   (elType, id, folderId, sourceDataType, destinationDataType, CombinedName)
     * @param moveTargetKind — тип цели ('root' / 'folder' / 'table')
     */
    async changeChildrenParent(
      items: any[],
      hoverParams: any,
      moveTargetKind?: MoveTargetKind,
    ): Promise<{ errorMessage?: string } | void> {
      const rootstore = rootStore()

      const targetKind: MoveTargetKind = moveTargetKind
        ?? (hoverParams.elType === BaseItemTypes.FOLDER ? 'folder' : 'table')

      this.moveElementsLocallySorted(items, hoverParams, targetKind);

      // Собираем массивы Identity по типам
      const tableElements: AnalyticsElementIdentity[] = []
      const charts: AnalyticsChartIdentity[] = []
      const folders: AnalyticsFolderIdentity[] = []

      for (const item of items) {
        const elType = item.elType as BaseItemTypes
        if (elType === BaseItemTypes.SELECTION || elType === BaseItemTypes.PROJECTION || elType === BaseItemTypes.LIST) {
          tableElements.push({
            ElementId: item.id,
            Kind: elType === BaseItemTypes.SELECTION ? AnalyticsElementKind.Selection
              : elType === BaseItemTypes.PROJECTION ? AnalyticsElementKind.Projection
                : AnalyticsElementKind.List,
            Source: elType === BaseItemTypes.PROJECTION ? item.sourceDataType : undefined,
            Destination: item.destinationDataType,
          })
        } else if (elType === BaseItemTypes.GRAPH || elType === BaseItemTypes.PIE_CHART || elType === BaseItemTypes.BAR_CHART) {
          const chartType = elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie
            : elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar
              : AnalyticsV2ChartType.Linear
          charts.push({
            ChartId: item.id,
            ChartType: chartType,
            Destination: item.destinationDataType,
          })
        } else if (elType === BaseItemTypes.FOLDER) {
          folders.push({
            FolderId: item.folderId,
            Segment: item.destinationDataType,
          })
        }
      }

      this.applyElementAction({
        Id: hoverParams.elType === BaseItemTypes.FOLDER ? undefined : hoverParams.id,
        FolderId: hoverParams.elType === BaseItemTypes.FOLDER ? hoverParams.folderId : undefined,
        elType: hoverParams.elType,
        SrcType: hoverParams.sourceDataType,
        DestType: hoverParams.destinationDataType,
      }, 'start-process')

      try {
        if (targetKind === 'root') {
          await this.moveAnalyticsToRootList({ TableElements: tableElements, Charts: charts, Folders: folders })
        } else if (targetKind === 'folder' && hoverParams.folderId != null && hoverParams.destinationDataType != null) {
          await this.moveAnalyticsToFolder({
            TableElements: tableElements,
            Charts: charts,
            Folders: folders,
            TargetFolder: { FolderId: Number(hoverParams.folderId), Segment: hoverParams.destinationDataType },
          })
        } else {
          const kind = hoverParams.elType === BaseItemTypes.SELECTION ? AnalyticsElementKind.Selection
            : hoverParams.elType === BaseItemTypes.PROJECTION ? AnalyticsElementKind.Projection
              : hoverParams.elType === BaseItemTypes.LIST ? AnalyticsElementKind.List
                : AnalyticsElementKind.Selection
          await this.moveAnalyticsToNewParent({
            TableElements: tableElements,
            Charts: charts,
            Folders: folders,
            TargetParentElement: {
              ElementId: hoverParams.id,
              Kind: kind,
              Source: hoverParams.elType === BaseItemTypes.PROJECTION ? hoverParams.sourceDataType : undefined,
              Destination: hoverParams.destinationDataType,
            },
          })
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => this.getGeneralAnalyticsData(rootstore.generalMode), 300)
        return
      }
    },

    /**
     * deleteEntity method. Backend API method for removing a selection or a projection
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async deleteEntity(arg: AnalyticsRequestData) {
      const rootstore = rootStore()

      // Оптимистично удаляем сам отчёт из локального reportsList сразу до запроса.
      // Для остальных типов локальное удаление выполняется в вызывающем коде (removeElementLocally).
      let removedReport: BaseListItem | null = null
      let removedReportIndex = -1
      if (arg.type === BaseItemTypes.REPORT && arg.id != null) {
        const arr = this.reportsList
        const idx = arr ? arr.findIndex((r: BaseListItem) => r.Id === arg.id) : -1
        if (idx !== -1) {
          removedReportIndex = idx
          removedReport = arr[idx]
          arr.splice(idx, 1)
        }
      }

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        let http_url = "";
        let params = {}
        switch (arg.type) {
          case BaseItemTypes.SELECTION:
            http_url = `Analytics/Selections/Remove/${arg.id}?selectionType=${arg.destinationType}`;
            break;
          case BaseItemTypes.LIST:
            http_url = `Analytics/Lists/Remove/${arg.id}?listType=${arg.destinationType}`;
            break;
          case BaseItemTypes.PROJECTION:
            http_url = `Analytics/Projections/Remove/${arg.id}?destination=${arg.destinationType}&projectionSource=${arg.sourceType}`;
            break;
          case BaseItemTypes.REPORT:
            http_url = `Analytics/Reports/Remove/${arg.id}`;
            break;
          case BaseItemTypes.GRAPH:
          case BaseItemTypes.PIE_CHART:
          case BaseItemTypes.BAR_CHART:
            http_url = `Analytics/Charts/Remove`;
            params = {
              ChartId: arg.id,
              ChartType: arg.type === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : 
                (arg.type === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : AnalyticsV2ChartType.Linear),
              Destination: arg.destinationType
            }
            console.log(params)
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        let response = undefined
        if (arg.type === BaseItemTypes.SELECTION || 
            arg.type === BaseItemTypes.PROJECTION || 
            arg.type === BaseItemTypes.LIST ||
            arg.type === BaseItemTypes.REPORT) {
          response = await http.delete(http_url);
          console.log(response)
        } else {
          response = await http.post(http_url, params);
          console.log(response)
        }
        if (response.status === 200 || response.status === 201) {
          // Локальное удаление уже произошло (для REPORT — оптимистично выше; для остальных
          // обычно через removeElementLocally в вызывающем коде). Ничего не обновляем.
          return { response_ok: true }
        }

        // Не-успешный код для отчёта — откатываем оптимистичное удаление и пересинхронизируемся.
        if (arg.type === BaseItemTypes.REPORT && removedReport && removedReportIndex !== -1) {
          this.reportsList.splice(removedReportIndex, 0, removedReport)
          setTimeout(() => {
            this.getGeneralAnalyticsData(rootstore.generalMode)
          }, 1000)
        }
      } catch (e) {
        console.log(JSON.stringify(e))

        // На исключении — пересинхронизируемся с сервером. Для REPORT перед этим откатываем
        // оптимистичное удаление, чтобы пользователь увидел отчёт обратно до прихода свежего листа.
        if (arg.type === BaseItemTypes.REPORT && removedReport && removedReportIndex !== -1) {
          this.reportsList.splice(removedReportIndex, 0, removedReport)
        }
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }

      return { response_ok: false }
    },

    async deleteEntities(items: any[]) {
      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        const tableElements: AnalyticsElementIdentity[] = []
        const charts: AnalyticsChartIdentity[] = []
        const folders: AnalyticsFolderIdentity[] = []
        const localKeys = new Set<string>()

        for (const item of items || []) {
          const elType = item?.elType ?? item?.Type
          const id = item?.id ?? item?.Id
          const folderId = item?.folderId ?? item?.FolderId
          const sourceType = item?.sourceDataType ?? item?.SrcType
          const destinationType = item?.destinationDataType ?? item?.DestType

          if (elType === BaseItemTypes.FOLDER && folderId != null && destinationType != null) {
            folders.push({
              FolderId: Number(folderId),
              Segment: destinationType,
            })
            const key = `folder:${folderId}:${destinationType}`
            if (!localKeys.has(key)) {
              localKeys.add(key)
              this.removeElementLocally({ elType, FolderId: Number(folderId), DestType: destinationType })
            }
            continue
          }

          if ((elType === BaseItemTypes.PIE_CHART || elType === BaseItemTypes.BAR_CHART || elType === BaseItemTypes.GRAPH) && id != null && destinationType != null) {
            charts.push({
              ChartId: Number(id),
              ChartType: elType === BaseItemTypes.PIE_CHART
                ? AnalyticsV2ChartType.Pie
                : (elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : AnalyticsV2ChartType.Linear),
              Destination: destinationType,
            })
            const key = `chart:${elType}:${id}:${destinationType}`
            if (!localKeys.has(key)) {
              localKeys.add(key)
              this.removeElementLocally({ elType, Id: Number(id), DestType: destinationType })
            }
            continue
          }

          if (id == null || destinationType == null) continue
          const kind = elType === BaseItemTypes.SELECTION
            ? AnalyticsElementKind.Selection
            : (elType === BaseItemTypes.PROJECTION ? AnalyticsElementKind.Projection : AnalyticsElementKind.List)
          tableElements.push({
            ElementId: Number(id),
            Kind: kind,
            Source: kind === AnalyticsElementKind.Projection ? sourceType : undefined,
            Destination: destinationType,
          })
          const key = `item:${elType}:${id}:${sourceType ?? ''}:${destinationType}`
          if (!localKeys.has(key)) {
            localKeys.add(key)
            this.removeElementLocally({ elType, Id: Number(id), SrcType: sourceType, DestType: destinationType })
          }
        }

        // После всех локальных удалений (splice внутри removeElementLocally происходит
        // через nextTick + rAF) сбрасываем глобальное выделение, если текущий выбранный
        // элемент или его родитель попал под массовое удаление (в т.ч. через удаление
        // содержащей его папки/выборки/проекции). Откладываем через nextTick + rAF,
        // чтобы проверка отработала после splice.
        nextTick(() => {
          requestAnimationFrame(() => {
            this.resetSelectionIfMissing();
          });
        });

        const response = await http.post(`Analytics/General/remove-elements`, {
          TableElements: tableElements,
          Charts: charts,
          Folders: folders,
        })

        if (response.status === 200 || response.status === 201) {
          return { response_ok: true }
        }
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      } catch (e) {
        console.log(JSON.stringify(e))
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }
      return { response_ok: false }
    },

    /**
     * cloneEntity method. Backend API method for cloning a selection, a projection or a graph
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async cloneEntity(arg: AnalyticsRequestData) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        let params = {}
        switch (arg.type) {
          case BaseItemTypes.SELECTION:
          case BaseItemTypes.PROJECTION:
          case BaseItemTypes.LIST:
            http_url = `Analytics/General/CloneElement`;
            params = {
              ElementId: arg.id,
              Source: arg.type && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(arg.type) ? null : arg.sourceType,
              Destination: arg.destinationType,
              Kind: arg.kind
            } as AnalyticsElementIdentity
            break;
          case BaseItemTypes.FOLDER:
            http_url = `Analytics/Folders/Clone`;
            params = {
              FolderId: arg.id,
              Segment: arg.destinationType
            }
            break;
          case BaseItemTypes.GRAPH:
          case BaseItemTypes.PIE_CHART:
          case BaseItemTypes.BAR_CHART:
            http_url = `Analytics/Charts/Clone`;
            params = {
              ChartId: arg.id,
              ChartType: arg.type === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : 
                (arg.type === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : AnalyticsV2ChartType.Linear),
              Destination: arg.destinationType
            }
            console.log(params)
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.post(http_url, params);
        console.log(response)
        // После clone не форсим раскрытие пути при повторном выделении после reload.
        suppressProgrammaticExpandAfterReload = true;
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)

        if (response.status === 200 || response.status === 201) {
          return { response_ok: false }
        }
      } catch (e) {
        console.log(JSON.stringify(e))

        // refresh to actual state
        suppressProgrammaticExpandAfterReload = true;
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }

      return { response_ok: false }
    },

    /**
     * renameEntity method. Backend API method for renaming a selection or a projection
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async renameEntity(arg: AnalyticsRequestData) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        let params = {}
        switch (arg.type) {
          case BaseItemTypes.SELECTION:
            params = {
              SelectionType: arg.destinationType,
              NewName: arg.NewName
            }
            http_url = `Analytics/Selections/Rename/${arg.id}`;
            break;
          case BaseItemTypes.LIST:
            params = {
              ListType: arg.destinationType,
              NewName: arg.NewName
            }
            http_url = `Analytics/Lists/Rename/${arg.id}`;
            break;
          case BaseItemTypes.PROJECTION:
            params = {
              Destination: arg.destinationType,
              ProjectionSource: arg.sourceType,
              NewName: arg.NewName
            }
            http_url = `Analytics/Projections/Rename/${arg.id}`;
            break;
          case BaseItemTypes.REPORT:
            params = {
              Id: arg.id,
              Name: arg.NewName
            }
            http_url = `Analytics/Reports/Rename`;
            break;
          case BaseItemTypes.BAR_CHART:
          case BaseItemTypes.PIE_CHART:
          case BaseItemTypes.GRAPH:
            params = {
              ChartIdentity: {
                ChartId: arg.id,
                ChartType: arg.type === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : 
                  (arg.type === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : AnalyticsV2ChartType.Linear),
                Destination: arg.destinationType
              },
              NewName: arg.NewName
            }
            http_url = `Analytics/Charts/Rename`;
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const response = await http.put(http_url, params);
        console.log(response)
        if (response.status === 200 || response.status === 201) {

          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))

        // refresh to actual state
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }

      return { response_ok: false }
    },

    /**
     * favoriteEntity method. Backend API method for renaming a selection or a projection
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async favoriteEntity(arg: AnalyticsRequestData, val: boolean) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        let params = {}
        switch (arg.type) {
          case BaseItemTypes.SELECTION:
            params = {
              Destination: arg.destinationType,
              ElementId: arg.id,
              Kind: AnalyticsElementKind.Selection
            }
            http_url = `Analytics/favorite/${val ? 'set' : 'unset'}`;
            break;
          case BaseItemTypes.LIST:
            params = {
              Destination: arg.destinationType,
              ElementId: arg.id,
              Kind: AnalyticsElementKind.List
            }
            http_url = `Analytics/favorite/${val ? 'set' : 'unset'}`;
            break;
          case BaseItemTypes.PROJECTION:
            params = {
              Destination: arg.destinationType,
              Source: arg.sourceType,
              ElementId: arg.id,
              Kind: AnalyticsElementKind.Projection
            }
            http_url = `Analytics/favorite/${val ? 'set' : 'unset'}`;
            break;
          case BaseItemTypes.GRAPH:
          case BaseItemTypes.PIE_CHART:
          case BaseItemTypes.BAR_CHART:
            params = {
              ChartId: arg.id,
              ChartType: arg.type === BaseItemTypes.GRAPH ? AnalyticsV2ChartType.Linear :
                (arg.type === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Bar),
              Destination: arg.destinationType
            }
            http_url = `Analytics/favorite/charts/${val ? 'set' : 'unset'}`;
            break;
          case BaseItemTypes.FOLDER:
            params = {
              FolderId: arg.id,
              Segment: arg.destinationType
            }
            http_url = `Analytics/favorite/folders/${val ? 'set' : 'unset'}`;
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        const isChartOrFolder = arg.type === BaseItemTypes.GRAPH || arg.type === BaseItemTypes.PIE_CHART ||
          arg.type === BaseItemTypes.BAR_CHART || arg.type === BaseItemTypes.FOLDER;
        const response = isChartOrFolder
          ? await http.patch(http_url, params)
          : await http.put(http_url, params);
        console.log(response)
        if (response.status === 200 || response.status === 201) {

          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))

        // refresh to actual state
        setTimeout(() => {
          this.getGeneralAnalyticsData(rootstore.generalMode)
        }, 1000)
      }

      return { response_ok: false }
    },

    /**
     * deleteEntity method. Backend API method for removing a selection or a projection
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async addEntityToReport(arg: AnalyticsRequestData) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "Analytics/Reports/AddElement";
        let params = {}
        const addToNewReport = !arg.reportIds?.length || arg.reportIds.includes(0)
        const reportIds = (arg.reportIds || []).filter((id) => id != null && id !== 0)
        switch (arg.type) {
          case BaseItemTypes.SELECTION:
            params = {
              ReportIds: reportIds,
              AddToNewReport: addToNewReport,
              ElementType: AnalyticsReportElementAddType.TableElement,
              // выборка / проекция
              ElementIdentity: {
                ElementId: arg.id,
                Destination: arg.destinationType,
                Kind: AnalyticsElementKind.Selection
              }
            }
            break;
          case BaseItemTypes.LIST:
            params = {
              ReportIds: reportIds,
              AddToNewReport: addToNewReport,
              ElementType: AnalyticsReportElementAddType.TableElement,
              // выборка / проекция
              ElementIdentity: {
                ElementId: arg.id,
                Destination: arg.destinationType,
                Kind: AnalyticsElementKind.List
              }
            }
            break;
          case BaseItemTypes.PROJECTION:
            params = {
              ReportIds: reportIds,
              AddToNewReport: addToNewReport,

              ElementType: AnalyticsReportElementAddType.TableElement,
              // выборка / проекция
              ElementIdentity: {
                ElementId: arg.id,
                Source: arg.sourceType,
                Destination: arg.destinationType,
                Kind: AnalyticsElementKind.Projection
              }
            }
            break;
          case BaseItemTypes.GRAPH:
          case BaseItemTypes.PIE_CHART:
          case BaseItemTypes.BAR_CHART:
            params = {
              ReportIds: reportIds,
              AddToNewReport: addToNewReport,
              ElementType: AnalyticsReportElementAddType.Chart,
              // выборка / проекция
              ChartIdentity: {
                ChartId: arg.id,
                ChartType: arg.type === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : 
                  (arg.type === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : AnalyticsV2ChartType.Linear),
                Destination: arg.destinationType
              }
            }
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          throw new Error('Incorrect API URL');
        }

        let response = undefined
        response = await http.post(http_url, params);
        console.log(response)
        
        if (response.status === 200 || response.status === 201) {
          // Possibly switch to reports
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))

        // refresh to actual state
        // setTimeout(() => {
        //   this.getGeneralAnalyticsData(rootstore.generalMode)
        // }, 1000)
      }

      return { response_ok: false }
    },

    // Interface methods that don't use backend API

    moveItems(arg: any) {
      if (!arg.parent_id) {
        if (arg.type === BaseItemTypes.REPORT) {
          const item = this.reportsList[arg.dragIndex]
          this.reportsList.splice(arg.dragIndex, 1)
          this.reportsList.splice(arg.hoverIndex, 0, item)
        }
        return
      }
      if (arg.parent_type === BaseItemTypes.REPORT) {
        const parent = this.reportsList.find((r: any) => r.Id === arg.parent_id)
        const arr = parent?.ChildReportElements
        if (!arr || arg.dragIndex == null || arg.hoverIndex == null) return
        if (arg.dragIndex < 0 || arg.dragIndex >= arr.length) return
        if (arg.hoverIndex < 0 || arg.hoverIndex >= arr.length) return
        const item = arr[arg.dragIndex]
        if (!item) return
        arr.splice(arg.dragIndex, 1)
        arr.splice(arg.hoverIndex, 0, item)
      }
    },

    // 0 - false; 1 - true; 2 - true but not update;
    selectElement(element: any, el_to_find: any, anyways: boolean = false, for_editing: boolean = false): number {
      const folderMatch = element.elType === BaseItemTypes.FOLDER && el_to_find.elType === BaseItemTypes.FOLDER &&
        element.FolderId && element.FolderId === el_to_find.FolderId;
      const elementMatch = element.elType !== BaseItemTypes.FOLDER &&
          element.Id === el_to_find.Id && 
          (el_to_find.SrcType ? (element.SrcType === el_to_find.SrcType) : !element.SrcType) &&
          element.DestType === el_to_find.DestType &&
          element.elType === el_to_find.elType && 
          (!element.ElementId && !el_to_find.ElementId || element.ElementId === el_to_find.ElementId);
      if (folderMatch || elementMatch) {

            if (!element.isSelected || anyways) {
              element.isSelected = true
              if (for_editing) {
                element.isEditing = true
              }

              /**
               * Clear selected item settings and query if the element differs from this.currentElement
               */
              const isSameFolder = this.currentElement.elType === BaseItemTypes.FOLDER && el_to_find.elType === BaseItemTypes.FOLDER &&
                this.currentElement.FolderId && this.currentElement.FolderId === el_to_find.FolderId;
              const isSameElement = this.currentElement.elType !== BaseItemTypes.FOLDER &&
                this.currentElement.Id === el_to_find.Id &&
                !(el_to_find.SrcType ? (this.currentElement.SrcType !== el_to_find.SrcType) : this.currentElement.SrcType) &&
                this.currentElement.DestType === el_to_find.DestType &&
                this.currentElement.elType === el_to_find.elType &&
                ((!this.currentElement.ElementId && !el_to_find.ElementId) || this.currentElement.ElementId === el_to_find.ElementId);
              if (!isSameFolder && !isSameElement) {

                  console.log('clear at select')
                const setstore = settingsStore()
                const graphstore = graphSettingsStore()
                const querystore = queryStore()
                setstore.clearSettings()
                graphstore.clearSettings()
                
                if (element.elType === BaseItemTypes.FOLDER) {
                  querystore.clearSettings()
                } else if (element.elType === BaseItemTypes.SELECTION) {
                  querystore.clearGraph()
                  setstore.getSelectionSettings(element.Id, element.DestType)
                } else if (element.elType === BaseItemTypes.PROJECTION) {
                  querystore.clearGraph()
                  setstore.getProjectionSettings(element.Id, element.SrcType, element.DestType)
                } else if (element.elType === BaseItemTypes.LIST) {
                  querystore.clearGraph()
                  setstore.getListSettings(element.Id, element.DestType)
                } else if (element.elType === BaseItemTypes.REPORT) {
                  querystore.clearSettings()
                  element.isExpanded = true
                } else if (element.elType === BaseItemTypes.BAR_CHART || element.elType === BaseItemTypes.PIE_CHART || 
                    element.elType === BaseItemTypes.GRAPH) {
                      
                  querystore.clearTable()
                  graphstore.getGraphSettings({
                    ChartId: element.Id,
                    ChartType: element.elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : (
                      element.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Linear),
                    Destination: element.DestType
                  }, element.parent)
                }
              } else {
                console.log('select action - same, not clearing...')
                return 2;
              }
            }
            return 1;
      } else {
        element.isSelected = false
        element.isEditing = false
      }
      return 0;
    },

    renameElement(element: any, el_to_find: any): boolean {
      const folderMatch = element.elType === BaseItemTypes.FOLDER && el_to_find.element.elType === BaseItemTypes.FOLDER &&
        element.FolderId && element.FolderId === el_to_find.element.FolderId;
      const elementMatch = element.elType !== BaseItemTypes.FOLDER &&
          element.Id === el_to_find.element.Id && 
          (el_to_find.element.SrcType !== undefined && el_to_find.element.SrcType !== null ? (element.SrcType === el_to_find.element.SrcType) : !element.SrcType) &&
          element.DestType === el_to_find.element.DestType &&
          element.elType === el_to_find.element.elType;
      if (folderMatch || elementMatch) {
        
        /**
         * Update selected item name
         */
        
        if (element.elType !== BaseItemTypes.BREAK) {
          element.Name = el_to_find.newName
          const combnameArray: string[] = element.CombinedName.split('/')
          if (combnameArray && combnameArray.length) {
            const oldCombnamePart = combnameArray.join('/')
            combnameArray[combnameArray.length-1] = el_to_find.newName
            const newCombnamePart = combnameArray.join('/')
            element.CombinedName = newCombnamePart

            const a = function a(element: any, param: any): any | undefined {
              if (!element) return

              element.CombinedName = element.CombinedName.replace(param.oldCombnamePart, param.newCombnamePart)
              
              if (element.ChildSelections) {
                foreachRecursive(a, param, element.ChildSelections)
              }
              if (element.ChildProjections) {
                foreachRecursive(a, param, element.ChildProjections)
              }
              if (element.Charts) {
                foreachRecursive(a, param, element.Charts)
              }
              
              return
            }
            // replace first part recursively
            if (element.ChildSelections) {
              foreachRecursive(a, { oldCombnamePart, newCombnamePart }, element.ChildSelections)
            }
            if (element.ChildProjections) {
              foreachRecursive(a, { oldCombnamePart, newCombnamePart }, element.ChildProjections)
            }
            if (element.Charts) {
              foreachRecursive(a, { oldCombnamePart, newCombnamePart }, element.Charts)
            }
          }
          if (element.elType === BaseItemTypes.FOLDER && element.FolderId) {
            this.renameFolder(element.FolderId, element.DestType, el_to_find.newName);
          } else {
            this.renameEntity({
              id: element.Id,
              newParentCombinedName: el_to_find.element.CombinedName,
              type: el_to_find.element.elType,
              NewName: el_to_find.newName,
              sourceType: el_to_find.element.SrcType,
              destinationType: el_to_find.element.DestType,
              kind: getIdentityKind(el_to_find.element)
            });
          }
        } else {
          element.Name = el_to_find.newName
          const combnameArray: string[] = element.CombinedName.split('/')
          if (combnameArray && combnameArray.length) {
            const oldCombnamePart = combnameArray.join('/')
            combnameArray[combnameArray.length-1] = el_to_find.newName
            const newCombnamePart = combnameArray.join('/')
            element.CombinedName = newCombnamePart

            const a = function a(element: any, param: any): any | undefined {
              if (!element) return

              element.CombinedName = element.CombinedName.replace(param.oldCombnamePart, param.newCombnamePart)
              
              if (element.ChildSelections) {
                foreachRecursive(a, param, element.ChildSelections)
              }
              if (element.ChildProjections) {
                foreachRecursive(a, param, element.ChildProjections)
              }
              if (element.Charts) {
                foreachRecursive(a, param, element.Charts)
              }
              
              return
            }
            // replace first part recursively
            if (element.ChildSelections) {
              foreachRecursive(a, { oldCombnamePart, newCombnamePart }, element.ChildSelections)
            }
            if (element.ChildProjections) {
              foreachRecursive(a, { oldCombnamePart, newCombnamePart }, element.ChildProjections)
            }
            if (element.Charts) {
              foreachRecursive(a, { oldCombnamePart, newCombnamePart }, element.Charts)
            }
          }
          this.changeReportBreak({
            id: element.ElementId,
            newParentCombinedName: element.CombinedName,
            type: el_to_find.element.elType,
            NewName: el_to_find.newName
          });
        }
        return true
      }
      return false
    },

    favoriteElement(element: any, el_to_find: any): boolean {
      const folderMatch = element.elType === BaseItemTypes.FOLDER && el_to_find.element.elType === BaseItemTypes.FOLDER &&
        element.FolderId && element.FolderId === el_to_find.element.FolderId;
      const elementMatch = element.elType !== BaseItemTypes.FOLDER &&
          element.Id === el_to_find.element.Id && 
          (el_to_find.element.SrcType !== undefined && el_to_find.element.SrcType !== null ? (element.SrcType === el_to_find.element.SrcType) : !element.SrcType) &&
          element.DestType === el_to_find.element.DestType &&
          element.elType === el_to_find.element.elType;
      if (folderMatch || elementMatch) {
        
        /**
         * Update selected item name
         */
        
        if (element.elType === BaseItemTypes.FOLDER) {
          const newval = !element.IsFavorite
          element.IsFavorite = newval
          this.favoriteEntity({
            id: element.FolderId,
            newParentCombinedName: '',
            type: BaseItemTypes.FOLDER,
            destinationType: el_to_find.element.DestType
          }, newval);
        } else if (element.elType === BaseItemTypes.GRAPH || element.elType === BaseItemTypes.PIE_CHART || element.elType === BaseItemTypes.BAR_CHART) {
          const newval = !element.IsFavorite
          element.IsFavorite = newval
          this.favoriteEntity({
            id: element.Id,
            newParentCombinedName: '',
            type: el_to_find.element.elType,
            destinationType: el_to_find.element.DestType
          }, newval);
        } else if (element.elType === BaseItemTypes.SELECTION || element.elType === BaseItemTypes.PROJECTION || element.elType === BaseItemTypes.LIST) {
          const newval = !element.IsFavorite
          element.IsFavorite = newval
          this.favoriteEntity({
            id: element.Id,
            newParentCombinedName: '',
            type: el_to_find.element.elType,
            sourceType: el_to_find.element.SrcType,
            destinationType: el_to_find.element.DestType,
            kind: getIdentityKind(el_to_find.element)
          }, newval);
        }
        return true
      }
      return false
    },

    deleteElement(element: any, el_to_find: any): boolean {
      const folderMatch = element.elType === BaseItemTypes.FOLDER && el_to_find.elType === BaseItemTypes.FOLDER &&
        element.FolderId && element.FolderId === el_to_find.FolderId;
      const elementMatch = element.elType !== BaseItemTypes.FOLDER &&
          element.Id === el_to_find.Id && 
          (el_to_find.SrcType ? (element.SrcType === el_to_find.SrcType) : !element.SrcType) &&
          element.DestType === el_to_find.DestType &&
          element.elType === el_to_find.elType;
      if (folderMatch || elementMatch) {
        
        /**
         * Clear selected item settings and query if the element doesn't differ from this.currentElement
         */
        const isSameFolderD = this.currentElement.elType === BaseItemTypes.FOLDER && el_to_find.elType === BaseItemTypes.FOLDER &&
          this.currentElement.FolderId && this.currentElement.FolderId === el_to_find.FolderId;
        const isSameElementD = this.currentElement.elType !== BaseItemTypes.FOLDER &&
          this.currentElement.Id === el_to_find.Id &&
          (el_to_find.SrcType ? (this.currentElement.SrcType === el_to_find.SrcType) : !this.currentElement.SrcType) &&
          this.currentElement.DestType === el_to_find.DestType &&
          this.currentElement.elType === el_to_find.elType &&
          ((!this.currentElement.ElementId && !el_to_find.ElementId) || this.currentElement.ElementId === el_to_find.ElementId);
        if (isSameFolderD || isSameElementD) {

          const setstore = settingsStore();
          const graphstore = graphSettingsStore();
          const querystore = queryStore();
          setstore.clearSettings();
          graphstore.clearSettings();
          querystore.clearSettings();
        } else {
          console.log('delete action - same, not clearing...')
        }

        /**
         * Delete selected item
         */
        
        element.isProcessing = true;
        // Удаляем элемент из дерева локально с анимацией (соседние строки плавно съезжаются).
        // Раньше использовался element.isDeleting = true + v-show, который мгновенно скрывал элемент
        // через display:none — остальные элементы резко прыгали. removeElementLocally убирает элемент
        // из массива с задержкой через nextTick+rAF, что даёт CSS transition отработать.
        const elIdentity = element.elType === BaseItemTypes.FOLDER
          ? { elType: element.elType, FolderId: element.FolderId }
          : { elType: element.elType, Id: element.Id, SrcType: element.SrcType, DestType: element.DestType };
        this.removeElementLocally(elIdentity);

        if (element.elType === BaseItemTypes.FOLDER) {
          this.deleteFolder(element.FolderId, element.DestType);
        } else {
          this.deleteEntity({
            id: element.Id,
            oldParentId: element.parentId,
            type: element.elType,
            newParentCombinedName: '',
            sourceType: element.SrcType,
            destinationType: element.DestType,
            kind: getIdentityKind(element)
          });
        }

        // После локального удаления (splice внутри removeElementLocally происходит
        // через nextTick + rAF) проверяем, не остался ли currentElement / currentParentElement
        // выбранным на уже несуществующей сущности. Откладываем через тот же nextTick + rAF,
        // чтобы наша проверка выполнилась после splice.
        nextTick(() => {
          requestAnimationFrame(() => {
            this.resetSelectionIfMissing();
          });
        });

        return true
      }
      return false
    },

    cloneElement(element: any, el_to_find: any): boolean {
      const folderMatch = element.elType === BaseItemTypes.FOLDER && el_to_find.elType === BaseItemTypes.FOLDER &&
        element.FolderId && element.FolderId === el_to_find.FolderId;
      const elementMatch = element.elType !== BaseItemTypes.FOLDER &&
          element.Id === el_to_find.Id && 
          (el_to_find.SrcType ? (element.SrcType === el_to_find.SrcType) : !element.SrcType) &&
          element.DestType === el_to_find.DestType &&
          element.elType === el_to_find.elType;
      if (folderMatch || elementMatch) {
        
        /**
         * Clone selected item
         */
        
        element.isProcessing = true;
        if (element.elType === BaseItemTypes.FOLDER) {
          this.cloneEntity({
            id: element.FolderId,
            type: element.elType,
            destinationType: element.DestType,
            newParentCombinedName: '',
          });
        } else {
          this.cloneEntity({
            id: element.Id,
            newParentCombinedName: element.CombinedName,
            oldParentId: element.parentId,
            type: element.elType,
            sourceType: element.SrcType,
            destinationType: element.DestType,
            kind: getIdentityKind(element)
          });
        }
        // const setstore = settingsStore()
        // setstore.clearSettings()
        return true
      }
      return false
    },

    processElement(element: any, el_to_find: any, status: boolean): boolean {
      const folderMatch = element.elType === BaseItemTypes.FOLDER && el_to_find.elType === BaseItemTypes.FOLDER &&
        element.FolderId && element.FolderId === el_to_find.FolderId;
      const elementMatch = element.elType !== BaseItemTypes.FOLDER &&
          element.Id === el_to_find.Id && 
          (el_to_find.SrcType !== undefined && el_to_find.SrcType !== null ? (element.SrcType === el_to_find.SrcType) : !element.SrcType) &&
          element.DestType === el_to_find.DestType &&
          element.elType === el_to_find.elType;
      if (folderMatch || elementMatch) {
        
        /**
         * Set item processing status
         */
        
        element.isProcessing = status;
        return true
      }
      return false
    },

    /**
     * applyElementAction function. 
     * @param arg - element being selected. Passed from a list item node.
     */

    applyElementAction(
      arg: any,
      action_mode: string,
      anyways: boolean = false,
      for_editing: boolean = false,
      selectionSource: 'user' | 'programmatic' = 'programmatic',
    ) {
      const that = this
      let something_selected: number = 0;
      let something_selected_local: number = 0;
      const shouldForceExpandPath = action_mode === 'select' && selectionSource === 'programmatic';
      const expandAncestors = (ancestors: any[]) => {
        if (!shouldForceExpandPath || !Array.isArray(ancestors)) return;
        ancestors.forEach((ancestor) => {
          if (ancestor) ancestor.isExpanded = true;
        });
      };
     
      // unified list (folders + all types)

      foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
        if (!element) return
      
        switch (action_mode) {
          case 'select':
            something_selected_local = that.selectElement(element, el_to_find.el_to_find, anyways, for_editing);
            if (something_selected_local === 1) {
              that.currentElement = element
              that.currentParentElement = el_to_find.parent
              expandAncestors(el_to_find.ancestors || []);
              something_selected = something_selected_local;
            } else if (something_selected_local === 2) {
              expandAncestors(el_to_find.ancestors || []);
              something_selected = something_selected_local;
            }
          break;
          case 'rename':
            if (that.renameElement(element, el_to_find.el_to_find)) { return }
          break;
          case 'favorite':
            if (that.favoriteElement(element, el_to_find.el_to_find)) { return }
          break;
          case 'delete':
            if (that.deleteElement(element, el_to_find.el_to_find)) { return }
          break;
          case 'clone':
            if (that.cloneElement(element, el_to_find.el_to_find)) { return }
          break;
          case 'start-process':
            if (that.processElement(element, el_to_find.el_to_find, true)) { return }
          break;
          case 'end-process':
            if (that.processElement(element, el_to_find.el_to_find, false)) { return }
          break;
          default:
            break;
        }
        
        if (element.FolderChildren) {
          foreachRecursive(a, {
            el_to_find: el_to_find.el_to_find,
            parent: element,
            ancestors: [...(el_to_find.ancestors || []), element],
          }, element.FolderChildren)
        }
        if (element.ChildSelections) {
          foreachRecursive(a, {
            el_to_find: el_to_find.el_to_find,
            parent: element,
            ancestors: [...(el_to_find.ancestors || []), element],
          }, element.ChildSelections)
        }
        if (element.ChildProjections) {
          foreachRecursive(a, {
            el_to_find: el_to_find.el_to_find,
            parent: element,
            ancestors: [...(el_to_find.ancestors || []), element],
          }, element.ChildProjections)
        }
        if (element.Charts) {
          foreachRecursive(a, {
            el_to_find: el_to_find.el_to_find,
            parent: element,
            ancestors: [...(el_to_find.ancestors || []), element],
          }, element.Charts)
        }
        return
      }, { el_to_find: arg, parent: undefined, ancestors: [] }, this.unifiedList)

      // root reports

      foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
        if (!element) return
      
        switch (action_mode) {
          case 'select':
            something_selected_local = that.selectElement(element, el_to_find.el_to_find, anyways, for_editing);

            if (something_selected_local === 1) {
              that.currentElement = element
              that.currentParentElement = el_to_find.parent;
              expandAncestors(el_to_find.ancestors || []);
              something_selected = something_selected_local;
            } else if (something_selected_local === 2) {
              expandAncestors(el_to_find.ancestors || []);
              something_selected = something_selected_local;
            }
          break;
          case 'rename':
            if (that.renameElement(element, el_to_find.el_to_find)) { return }
          break;
          case 'delete':
            if (that.deleteElement(element, el_to_find.el_to_find)) { return }
          break;
          case 'clone':
            if (that.cloneElement(element, el_to_find.el_to_find)) { return }
          break;
          case 'start-process':
            if (that.processElement(element, el_to_find.el_to_find, true)) { return }
          break;
          case 'end-process':
            if (that.processElement(element, el_to_find.el_to_find, false)) { return }
          break;
          default:
            break;
        }

        if (element.ChildReportElements) {
          foreachRecursive(a, {
            el_to_find: el_to_find.el_to_find,
            parent: element,
            ancestors: [...(el_to_find.ancestors || []), element],
          }, element.ChildReportElements)
        }
      }, { el_to_find: arg, parent: undefined, ancestors: [] }, this.reportsList)

      if (action_mode === 'select' && something_selected === 0) {
        // console.log('returned '+something_selected)

        that.currentElement = {} as BaseListItem
        that.currentParentElement = {} as BaseListItem;

        // console.log('clear at returned')

        const setstore = settingsStore()
        const graphstore = graphSettingsStore()
        const querystore = queryStore()
        setstore.clearSettings()
        graphstore.clearSettings()
        querystore.clearSettings()
      } else {
        if (this.scrollAfter) {
          this.setScrollAfter(false)

          this.setTriggerScroll(true);
          const that = this
          setTimeout(() => {
            that.setTriggerScroll(false);
          },10)
        }
      }
    },

    clearGeneralList() {
      // console.log('clear at general')

      this.reportsList.splice(0)
      this.unifiedList.splice(0)
      this.currentElement = {} as BaseListItem
      this.currentParentElement = {} as BaseListItem

      const setstore = settingsStore()
      const graphstore = graphSettingsStore()
      const querystore = queryStore()
      setstore.clearSettings()
      graphstore.clearSettings()
      querystore.clearSettings()
    },

    clearCurrentSelection() {
      const clearUnifiedNode = (node: any) => {
        if (!node) return;
        node.isSelected = false;
        node.isEditing = false;
        ['FolderChildren', 'ChildSelections', 'ChildProjections', 'Charts'].forEach((key) => {
          const arr = node[key];
          if (Array.isArray(arr)) arr.forEach(clearUnifiedNode);
        });
      };

      const clearReportNode = (node: any) => {
        if (!node) return;
        node.isSelected = false;
        node.isEditing = false;
        ['ChildReportElements', 'Charts'].forEach((key) => {
          const arr = node[key];
          if (Array.isArray(arr)) arr.forEach(clearReportNode);
        });
      };

      this.unifiedList.forEach(clearUnifiedNode);
      this.reportsList.forEach(clearReportNode);

      this.currentElement = {} as BaseListItem;
      this.currentParentElement = {} as BaseListItem;
      this.setSelectedAfter(reactive({} as BaseListItem));
      this.setScrollAfter(false);
      this.setTriggerScroll(false);

      const setstore = settingsStore();
      const graphstore = graphSettingsStore();
      const querystore = queryStore();
      setstore.abortAllPending();
      graphstore.abortAllPending();
      querystore.abortAllPending();
      setstore.clearSettings();
      graphstore.clearSettings();
      querystore.clearSettings();
    },

    /**
     * isIdentityInTrees — проверяет, существует ли identity в unifiedList или reportsList.
     * Используется для определения, не «висит» ли currentElement / currentParentElement
     * на уже удалённой сущности.
     *
     * Сравнение строго по полям: для папки — elType + FolderId; для остальных —
     * elType + Id + SrcType + DestType + ElementId (последний — для дочерних элементов отчёта).
     * Логика идентична removeElementLocally / deleteElement.
     */
    isIdentityInTrees(identity: any): boolean {
      if (!identity || !identity.elType) return true;

      const matchEl = (item: any): boolean => {
        if (!item) return false;
        if (identity.elType === BaseItemTypes.FOLDER) {
          return item.elType === BaseItemTypes.FOLDER && item.FolderId === identity.FolderId;
        }
        return item.elType === identity.elType &&
          item.Id === identity.Id &&
          ((identity.SrcType != null) ? item.SrcType === identity.SrcType : !item.SrcType) &&
          item.DestType === identity.DestType &&
          ((!identity.ElementId && !item.ElementId) || item.ElementId === identity.ElementId);
      };

      const findInUnified = (node: any): boolean => {
        if (!node) return false;
        if (matchEl(node)) return true;
        const keys = ['FolderChildren', 'ChildSelections', 'ChildProjections', 'Charts'];
        for (const key of keys) {
          const arr = node[key];
          if (Array.isArray(arr)) {
            for (const child of arr) {
              if (findInUnified(child)) return true;
            }
          }
        }
        return false;
      };

      const findInReport = (node: any): boolean => {
        if (!node) return false;
        if (matchEl(node)) return true;
        const keys = ['ChildReportElements', 'Charts'];
        for (const key of keys) {
          const arr = node[key];
          if (Array.isArray(arr)) {
            for (const child of arr) {
              if (findInReport(child)) return true;
            }
          }
        }
        return false;
      };

      for (const node of (this.unifiedList || [])) {
        if (findInUnified(node)) return true;
      }
      for (const node of (this.reportsList || [])) {
        if (findInReport(node)) return true;
      }
      return false;
    },

    /**
     * resetSelectionIfMissing — если currentElement (или currentParentElement для графиков)
     * больше не существует в дереве, выполняем полный сброс выделения через clearCurrentSelection
     * (как при ручном «отщёлкивании»). Без этого после удаления выбранным остаётся уже не
     * существующий элемент, и пользовательские действия (выборки, проекции, отчёты, settings)
     * продолжают идти по нему, вызывая ошибки.
     *
     * Важно: вызывать ПОСЛЕ того, как локальное удаление из дерева фактически произошло.
     * removeElementLocally делает splice асинхронно через nextTick + requestAnimationFrame,
     * поэтому в местах удаления вызов оборачивается в тот же nextTick + rAF, чтобы наша
     * проверка выполнилась после splice.
     */
    resetSelectionIfMissing() {
      const cur = this.currentElement as any;
      const parent = this.currentParentElement as any;
      const curEmpty = !cur || !cur.elType;
      const parentEmpty = !parent || !parent.elType;
      if (curEmpty && parentEmpty) return;

      const curMissing = !curEmpty && !this.isIdentityInTrees(cur);
      const parentMissing = !parentEmpty && !this.isIdentityInTrees(parent);
      if (curMissing || parentMissing) {
        this.clearCurrentSelection();
      }
    },
  },

  getters: {
    isLoading(state): boolean {
      return state.loading
    },

    globalFilterMode(state): boolean {
      return state.filterMode
    },

    analyzedListForChart(state): any[] {
      const res: any[] = []
      const used = new Set<string>()

      const resolveElType = (el: any): number | undefined => {
        if (el.elType !== undefined) return el.elType
        if (el.Identity) {
          const k = (el.Identity as any).Kind
          if (k === AnalyticsElementKind.Selection) return BaseItemTypes.SELECTION
          if (k === AnalyticsElementKind.List) return BaseItemTypes.LIST
          if (k === AnalyticsElementKind.Projection) return BaseItemTypes.PROJECTION
        }
        if (el.FolderId !== undefined || el.Folders !== undefined) return BaseItemTypes.FOLDER
        return undefined
      }

      const CANDIDATE_TYPES = [BaseItemTypes.SELECTION, BaseItemTypes.LIST, BaseItemTypes.PROJECTION]

      const pushCandidate = (element: any) => {
        if (!element) return
        const elType = resolveElType(element)
        if (!CANDIDATE_TYPES.includes(elType as number)) return
        const id = element.Id ?? element.Identity?.ElementId
        const destType = element.DestType ?? element.Identity?.Destination
        const srcType = element.SrcType ?? element.Identity?.Source
        const kind = elType === BaseItemTypes.SELECTION ? AnalyticsElementKind.Selection
          : elType === BaseItemTypes.LIST ? AnalyticsElementKind.List
          : AnalyticsElementKind.Projection
        const identity: AnalyticsElementIdentity = {
          ElementId: id,
          Kind: kind,
          Source: srcType ?? undefined,
          Destination: destType,
        }
        const key = `${kind}:${id}:${srcType ?? ''}:${destType}`
        if (used.has(key)) return
        used.add(key)
        res.push({ Id: id, Name: element.Name, Identity: identity })
      }

      // Все массивы-контейнеры дочерних элементов (mapped + raw API).
      // Из-за позиционной вставки (insertElementLocally) любой тип элемента
      // может оказаться в любом из этих массивов.
      const MAPPED_KEYS = ['Charts', 'ChildProjections', 'ChildSelections', 'FolderChildren'] as const
      const RAW_KEYS = ['Selections', 'AnalyticsLists', 'Projections', 'Folders'] as const
      const ALL_KEYS = [...MAPPED_KEYS, ...RAW_KEYS] as const

      const walkSelectionChildren = (node: any) => {
        if (!node) return
        for (const key of ALL_KEYS) {
          const arr = node[key]
          if (!arr?.length) continue
          for (const child of arr) {
            if (!child) continue
            const t = resolveElType(child)
            if (t === BaseItemTypes.SELECTION || t === BaseItemTypes.LIST) {
              pushCandidate(child)
              walkSelectionChildren(child)
            }
          }
        }
      }

      const walkFolder = (folder: any) => {
        if (!folder) return
        for (const key of ALL_KEYS) {
          const arr = folder[key]
          if (!arr?.length) continue
          for (const child of arr) {
            if (!child) continue
            const t = resolveElType(child)
            if (t === BaseItemTypes.FOLDER) {
              walkFolder(child)
            } else if (t === BaseItemTypes.SELECTION || t === BaseItemTypes.LIST) {
              pushCandidate(child)
              walkSelectionChildren(child)
            } else if (t === BaseItemTypes.PROJECTION) {
              pushCandidate(child)
            }
          }
        }
      }

      const currentEl = state.currentElement
      const isChart = currentEl?.elType !== undefined &&
        [BaseItemTypes.GRAPH, BaseItemTypes.PIE_CHART, BaseItemTypes.BAR_CHART].includes(currentEl.elType)

      // Поиск родителя графика по всем массивам unifiedList (график может быть в любом массиве)
      const findChartParent = (items: any[]): any | null => {
        for (const item of items) {
          for (const key of MAPPED_KEYS) {
            const arr = item[key]
            if (!arr?.length) continue
            if (arr.some((c: any) =>
              c.Id === currentEl.Id &&
              c.DestType === currentEl.DestType &&
              c.elType === currentEl.elType
            )) return item
          }
          for (const key of MAPPED_KEYS) {
            const arr = item[key]
            if (!arr?.length) continue
            const r = findChartParent(arr)
            if (r) return r
          }
        }
        return null
      }

      let parent: any = null
      if (isChart) {
        parent = findChartParent(state.unifiedList)
      }

      if (!parent || parent.elType === undefined) {
        const storedParent = state.currentParentElement
        parent = (storedParent?.elType !== undefined)
          ? storedParent
          : (currentEl?.parent ?? null)
      }

      if (!parent || parent.elType === undefined) return res

      if (parent.elType === BaseItemTypes.FOLDER) {
        walkFolder(parent)
      } else {
        pushCandidate(parent)
        walkSelectionChildren(parent)
      }

      return res
    },

    selectedItem(state): BaseListItem {
      return state.currentElement
    },

    selectedItemParent(state): BaseListItem {
      return state.currentParentElement
    },

    getPrecalcCount(state): number {
      return state.precalculatedCount;
    },

    ReportsList(state): BaseListItem[] {
      // @ts-ignore
      return state.reportsList
    },

    UnifiedList(state): BaseListItem[] {
      // @ts-ignore
      return state.unifiedList
    },

    accessRights(state): AnalyticsAccessContainer {
      return state.accessContainer
    }
  }
});
